import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  CASE_KEY,
  CASES_KEY,
  casePageUrl,
  createCase,
  editorSeed,
  editorSeedKey,
  EMPTY_STATE,
  extractionPageUrl,
  fromEditor,
  manualOutline,
  noteStandWritten,
  postBasis,
  postVersion,
  ProblemError,
  RECORD_NOT_FOUND,
  reviewOf,
  StaleVersionError,
  standSeed,
  standSeedKey,
  TakeOverConflict,
  TakeOverRefused,
  useCase,
  useExtraction,
  type DemoRunResult,
} from "@/api";
import { Bone } from "@/components/Bone";
import { Button } from "@/components/Button";
import { formChangedPath } from "@/components/ChangeFormDialog";
import { releaseLossSentence } from "@/components/ConfirmReleaseLoss";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { DisputedFigureNotice, useDisputedFindings } from "@/components/DisputedFigureNotice";
import { FigureLossAsk } from "@/components/FigureLossAsk";
import { LengthDoubt } from "@/components/LengthDoubt";
import { MaximizeButton } from "@/components/MaximizeButton";
import { OutlineDraft } from "@/components/OutlineDraft";
import { SelectField } from "@/components/fields/SelectField";
import { TextField } from "@/components/fields/TextField";
import { Skeleton } from "@/components/PageSkeleton";
import { PageTitle } from "@/components/PageTitle";
import { SegmentedControl, type SegmentOption } from "@/components/SegmentedControl";
import { StateMessage } from "@/components/StateMessage";
import { useScreenToast } from "@/components/Toast";
import { Tooltip } from "@/components/Tooltip";
import {
  cornerRemoval,
  deleteWall,
  draftFromSeed,
  draftFromStand,
  figuresLost,
  appendTarget,
  appendWall,
  notchTarget,
  pullCorner,
  removeCorner,
  sheetSize,
  startShapeOf,
  walkVertices,
  type Draft,
  type DraftChamfer,
  type LostFigure,
  type StartShape,
} from "@/lib/draft";
import { FACADE_CANCELLED, FACADE_SENT, facadeEditability, refusedAsCancelled } from "@/lib/facadeEditability";
import {
  angleOf,
  chamferProblem,
  chamferWallNumber,
  diagonalMm,
  displayWallNumber,
  dropStrandedChamfers,
  legsFromAngle,
  removeChamfer,
  setChamfer,
  usableMm,
} from "@/lib/fase";
import { formatDateTime, formatMetres, formatMm } from "@/lib/format";
import { LENGTH_REFUSALS, parseLengthMm, wallLengthDoubt, type LengthRefusal } from "@/lib/length";
import { measureWord } from "@/lib/outline";
import {
  clearStoredDraft,
  draftStorageKey,
  draftStorageWorks,
  loadStoredDraft,
  saveStoredDraft,
  type DraftContext,
  type StoredDraft,
} from "@/lib/outlineDraftStorage";
import { clearResolutions, loadResolutions } from "@/lib/reviewResolutions";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useMaximize } from "@/lib/useMaximize";
import { keepingFocus, undoKeys, useUndoKeys } from "@/lib/useUndoKeys";
import { DEFAULT_START_SHAPE, draftFingerprint, startDraft, useOutlineDraft } from "@/store/outlineDraft";

import { capCaseTitle, caseTitleFrom, MANUAL_CASE_TITLE } from "./caseTitle";
import styles from "./OutlineDraftScreen.module.css";
import { OutlineHelp } from "./OutlineHelp";
import { describeDraftStep } from "./undoLabel";

/** Stands in for the one render before the store has been asked to `ensure`. */
const FIRST_FRAME = startDraft(DEFAULT_START_SHAPE);

/**
 * Which Vorgang a drawing under way was begun for (R-4, UX gate round 1 #2),
 * keyed by the drawing's first step — the rectangle `begin` lays, which
 * `restart` keeps and every new `begin` replaces. Not in the store: nothing
 * but this screen asks, and a drawing begun anywhere else (the start page,
 * a test's seed) is simply not in the map — „for no Vorgang".
 */
const boundTo = new WeakMap<Draft, string>();

/**
 * Opened for a Vorgang, the drawing is that Vorgang's — begun afresh for it,
 * kept for it. Opened from a reading (`seeded`), it begins with the outline
 * the reading proposed and is kept under that reading's own key: coming back
 * finds the corrections, a newer reading or another candidate starts anew.
 */
function ensureDraftFor(caseId: string | null, seeded?: { key: string; draft: Draft }): void {
  const store = useOutlineDraft.getState();
  const first = store.history[0];
  const owner = first !== undefined ? (boundTo.get(first) ?? null) : null;
  if (seeded !== undefined) {
    if (first !== undefined && owner === seeded.key) return;
    store.beginWith(seeded.draft);
    boundTo.set(seeded.draft, seeded.key);
    return;
  }
  if (caseId === null) {
    // Back on the free drawing after one for a Vorgang: that one is not a
    // new Vorgang's first draft.
    if (owner !== null) store.beginShape(DEFAULT_START_SHAPE);
    else store.ensure();
    return;
  }
  if (first !== undefined && owner === caseId) return;
  startOverFor(caseId, DEFAULT_START_SHAPE);
}

/**
 * A new start of the given form (Task 17), still the drawing of the Vorgang
 * it was for — `boundTo` is keyed by the first step, which this replaces.
 */
function startOverFor(caseId: string | null, shape: StartShape): void {
  const store = useOutlineDraft.getState();
  store.beginShape(shape);
  const seed = useOutlineDraft.getState().history[0];
  if (seed !== undefined && caseId !== null) boundTo.set(seed, caseId);
}

/** „Eine Wand · Rechteck“ — the two starts of a new drawing (Task 17), the most frequent one first. */
const START_OPTIONS: readonly SegmentOption<StartShape>[] = [
  { value: "wall", label: "Eine Wand" },
  { value: "rectangle", label: "Rechteck" },
];
/**
 * Either start, untouched (Task 17): nothing in it worth offering back — the
 * kept copy of a rectangle nobody touched, on a board that now starts with
 * one wall, is not a drawing anybody lost.
 */
const BARE_STARTS: ReadonlySet<string> = new Set(
  START_OPTIONS.map((option) => draftFingerprint(startDraft(option.value))),
);
/** The visible name of the choice of start. */
const START_LABEL = "draft-start-label";
/** Said once a new start is on the board — the board changed under the focus. */
const STARTED: Record<StartShape, string> = {
  wall: "Neu begonnen mit einer Wand, ohne Maß.",
  rectangle: "Neu begonnen mit einem Rechteck: vier Wände, ohne Maß.",
};
/** What a new start and „Von vorn“ throw away, said in their question. */
const LOST = "Die gezeichnete Form, getippte Maße, Beschriftungen und Fasen gehen verloren.";
/** The question before a new start throws away what was typed. */
const START_OVER: Record<StartShape, string> = {
  wall: "Mit einer Wand neu beginnen?",
  rectangle: "Mit einem Rechteck neu beginnen?",
};

/**
 * Drawings that went through „Übernehmen“ (Welle 1, idea 12). The draft
 * stays in this tab's memory afterwards, but it is no longer anything to
 * lose: its browser copy goes once the Stand is written (`keptDone`), it is
 * not written there again, and leaving the page does not ask about it. A
 * new gesture makes a new draft, which is not in here.
 */
const handedOver = new WeakSet<Draft>();

/** „6 Wände, davon 2 bemaßt“ — what a kept drawing holds, said before anybody brings it back. */
function keptSummary(draft: Draft): string {
  const count = draft.walls.length;
  const measured = draft.walls.filter((each) => each.lengthMm !== null).length;
  if (count === 1) return measured === 1 ? "1 Wand, bemaßt" : "1 Wand, ohne Maß";
  const which = measured === 0 ? "keine bemaßt" : measured === count ? "alle bemaßt" : `davon ${measured} bemaßt`;
  return `${count} Wände, ${which}`;
}

/** The foot's second sentence: where the drawing lives until „Übernehmen“. */
const KEPT_NOTE = "Der Entwurf wird bis dahin nur in diesem Browser gesichert und lässt sich nach einem Neuladen wiederherstellen.";
/** Said alone, under the surface (Task 10) — so it names „Übernehmen" itself;
 * „bis dahin" had nothing there to point back to (Task 12). */
const NOT_KEPT_NOTE = "Der Entwurf lebt bis zum Übernehmen nur in diesem Fenster, ein Neuladen beginnt von vorn.";

/**
 * Holds „Übernehmen" and says why — a Vorgang that has its first Stand, one
 * this person no longer holds, or one that has not answered yet.
 */
const HELD_REASON = "outline-held";

/** „Vorlage | Brett" — where everything stacks, the drawing and the board take turns. */
const VIEW_OPTIONS: readonly SegmentOption<"reference" | "board">[] = [
  { value: "reference", label: "Vorlage" },
  { value: "board", label: "Brett" },
];

/** The field „Name des Vorgangs“, beside „Übernehmen“ of a drawing that makes a new Vorgang (Task 14). */
const CASE_NAME = "draft-case-name";
const CASE_NAME_HINT = `Ohne Namen heißt er „${MANUAL_CASE_TITLE}“.`;
/** Said once the first press made the Vorgang and only its Stand failed: a new name would not reach it. */
const CASE_NAME_MADE = "Der Vorgang ist unter diesem Namen schon angelegt.";

/** A 409 on Stand 1 of an existing Vorgang: somebody was first. */
const STANDING = "Dieser Vorgang hat inzwischen einen Stand — ein Umriss von Hand wird nur zum ersten. Weiter geht es in der Planung.";
/** Where the side panel says what „Ecke entfernen“ does at the picked corner, or why not. */
const CORNER_HINT = "draft-corner-hint";
/** The stage's title, which names the stage (UX review 26.10.2026, B-6). */
const STAGE_TITLE = "draft-stage-title";
/** What every line of the drawing obeys — said once, under the surface. */
const AXIS = "Achsparallel — jede Wand läuft waagerecht oder senkrecht.";
const AXIS_FASE = "Achsparallel — jede Wand läuft waagerecht oder senkrecht, nur eine Fase schräg.";
/** The stage's bones' names for a screen reader, while the form is on its way (Task 10, B-4). */
const STAND_LOADING = "Form des Stands wird geladen";
const READING_LOADING = "Umriss aus der Lesung wird geladen";

/**
 * „Ecke entfernen“ in words (Kauf-Test 11.10.2026), by what `cornerRemoval`
 * says of the picked corner. The refusals say what to do instead.
 */
const CORNER_HINTS: Record<ReturnType<typeof cornerRemoval>, string> = {
  ok: "Die beiden Wände hier liegen in einer Linie — ohne die Ecke werden sie eine Wand, ihre Maße zusammengezählt.",
  turns: "Eine Ecke fällt weg, wenn ihre Wände in einer Linie liegen — zieh die Wand bündig, dann verschwindet die Stufe.",
  end: "Am offenen Ende hängt nur eine Wand. Zieh den Endpunkt oder drück „Wand anhängen“, um den Zug zu verlängern; zum Kürzen entferne die Wand.",
  tooFew: "Ein Umlauf braucht mindestens vier Wände.",
};

// ── Fase (design 2026-10-18 §6.5) ──────────────────────────────────────────

/** Where the Fase section says why „Fase setzen“ waits. */
const FASE_REASON = "draft-fase-reason";
/** The Fase section's heading, which names its group. */
const FASE_HEAD = "draft-fase-head";
/** Nothing to work a chamfer out from yet. */
const FASE_EMPTY = "Erst Winkel und Länge eintragen.";
/** The fields describe the chamfer that is already on this corner. */
const FASE_STANDS = "Die Fase ist so gesetzt.";
/**
 * A shape gesture on the board that would take the figure off a wall, held for
 * its question (spec 2026-10-28 §3.4, „Maß geht verloren“).
 */
interface FigureAsk {
  /** A new mount for every ask — it takes the focus on mount. */
  id: number;
  /** The shape before the gesture, which Abbrechen returns to. */
  base: Draft;
  /** The shape after it. */
  next: Draft;
  lost: LostFigure[];
  /**
   * Whether `next` is on the stack already: a drag's frames are, as a preview
   * the person sees; a key press has not been taken.
   */
  applied: boolean;
  /** What follows once the step counts (a merged wall is picked, a corner deselected). */
  taken: (() => void) | null;
  /** The button that asked, where one did: the focus goes back to it. */
  opener: HTMLButtonElement | null;
  /** The step on top of the stack while it is asked — any other step ends the question. */
  at: Draft | undefined;
  /** The wall to put the focus back on. */
  wall: number | null;
}
/**
 * A change of form took the chamfers with it — `pullCorner`, `removeCorner`
 * and `deleteWall` drop them all, a push those on the walls it stretched.
 * Said once, never silently (design §8).
 */
const FASEN_GONE = "Fasen entfernt — die Form hat sich geändert.";
const ANGLE_REFUSAL = "Ein Winkel von 1 bis 89 Grad, z. B. 45.";
/** „Übernehmen“ held: a typed figure stranded a chamfer, which has just been taken off. */
const STRANDED_HOLD = "Eine Fase passte nicht mehr zu den Maßen und ist entfernt — prüf die Form und übernimm noch einmal.";
/** „Übernehmen“ held: a wall longer than any facade waits for its answer (`wallLengthDoubt`). */
function longWallHold(wallNumber: number, mm: number): string {
  return `Wand ${wallNumber} misst ${formatMetres(mm)} — beantworte die Frage am Maß und übernimm dann noch einmal.`;
}
/** „Kalibrieren“ held for the same reason. */
const STRANDED_CALIBRATE_HOLD =
  "Eine Fase passte nicht mehr zu den Maßen und ist entfernt — prüf die Form und kalibriere noch einmal.";
/** The length refusals for a leg or a slant — „keine Wand“ would be the wrong word there. */
const FASE_LENGTH_REFUSALS: Record<Exclude<LengthRefusal, "empty">, string> = {
  unreadable: "Kein Maß erkannt — z. B. 500.",
  too_short: "Unter 100 mm — prüf die Zahl.",
  too_long: "Über 100 m — prüf die Zahl.",
};

/** The angle field: whole or decimal degrees, comma or point, „°“ allowed — from 1 to 89. */
function parseAngle(text: string): { ok: true; deg: number } | { ok: false; empty: boolean } {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: false, empty: true };
  const match = /^(\d+(?:[.,]\d+)?)\s*°?$/.exec(trimmed);
  if (match === null) return { ok: false, empty: false };
  const deg = Number(match[1]!.replace(",", "."));
  return deg >= 1 && deg <= 89 ? { ok: true, deg } : { ok: false, empty: false };
}

/** „Ecke n“ for the chamfer keyed by `wallIndex` — the editor's corner at that wall's end. */
function chamferCorner(draft: Draft, wallIndex: number): number {
  const vertex = wallIndex + 1;
  return (draft.closed && vertex === draft.walls.length ? 0 : vertex) + 1;
}

/** The chamfers as the hand path and „Form ändern …“ send them — never „Kalibrieren“, never a reading's Stand 1. */
function wireChamfers(draft: Draft): DraftChamfer[] {
  return (draft.chamfers ?? []).map(({ wallIndex, legAMm, legBMm }) => ({ wallIndex, legAMm, legBMm }));
}

/** A 409 on a reading that is no longer the newest (plan 4 §4, B3). */
const NEWER_READING = "Inzwischen liegt eine neuere Lesung vor. Öffne den Editor aus der neuen Lesung noch einmal.";

/** A 409 on a Basisstand against a Stand that is no longer the latest (spec 2026-10-15 §3). */
const STAND_MOVED = "Der Stand hat sich inzwischen geändert — öffne „Form ändern …“ neu.";

/**
 * What a refusal of the stand seed or the Basisstand says in its body
 * (`ConflictOut`): the reason codes, and the Stand the server is at. Read off
 * `problem.raw`, never off the sentence.
 */
function conflictOf(error: unknown): { reasons: string[]; latestVersionNo: number | null } | null {
  if (!(error instanceof ProblemError) || error.status !== 409) return null;
  const raw = error.problem.raw;
  if (typeof raw !== "object" || raw === null) return { reasons: [], latestVersionNo: null };
  const { reasons, latestVersionNo } = raw as { reasons?: unknown; latestVersionNo?: unknown };
  return {
    reasons: Array.isArray(reasons) ? reasons.filter((each): each is string => typeof each === "string") : [],
    latestVersionNo: typeof latestVersionNo === "number" ? latestVersionNo : null,
  };
}

/**
 * Where „Abbrechen“ leads from the editor seeded from a Stand: one step back
 * where the app itself brought somebody here (the board, the record), the
 * board of this Vorgang where the address was opened directly — never out of
 * the app. The print route's rule (`PrintRoute`).
 */
function stepBack(navigate: ReturnType<typeof useNavigate>, caseId: string): void {
  const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  if (index > 0) navigate(-1);
  else navigate(`/vorgang/${caseId}/planung`);
}

/**
 * The way in, in one sentence over the stage — and what else there is to
 * know about it, which waits under „?“ (UX review 26.10.2026, B-6: a lede of
 * two lines cost the surface its first screen).
 */
function ledeOf(
  way: "stand" | "reading" | "wall" | "shape",
  standNo: number | null,
  read: boolean,
): { sentence: string; rest: string } {
  if (way === "stand") {
    return {
      sentence: `Die Form aus Stand ${standNo ?? "…"}, mit jedem Maß — ändere, was nicht stimmt.`,
      rest: `Eine Wand, die du teilst oder verschiebst, gibt ihr Maß ab.${read ? " „gelesen“ heißt: so stand es auf dem Blatt." : ""}`,
    };
  }
  if (way === "reading") {
    return {
      sentence: "Der Umriss aus deiner Zeichnung — zieh die Ecken, bis die Form stimmt, und tipp die fehlenden Maße ein.",
      rest: "„gelesen“ heißt: so stand es auf dem Blatt.",
    };
  }
  if (way === "wall") {
    return {
      sentence: "Eine Wand reicht für eine Terrasse am Haus: Wand anklicken, Maß tippen, übernehmen.",
      rest: "Läuft der Rost um eine Ecke, zieh am Endpunkt weiter — oder klick ihn an und nimm „Wand anhängen“.",
    };
  }
  return {
    sentence: "Erst die Form, dann die Maße: zieh die Ecken, bis der Grundriss stimmt, und tipp die Maße danach ein.",
    rest: "Eine Wand, die du teilst oder verschiebst, gibt ihr Maß ab, weil es sie nicht mehr beschreibt.",
  };
}

/**
 * The stage's body in bones while the form of a Stand or the outline of a
 * reading is on its way (Task 10; UX review 26.10.2026, B-4): the surface at
 * its height and the tools' column beside it, in the workspace's own grid —
 * never „… wird geladen …“. The stage's head and „Übernehmen“ stand around
 * it, held until the form is there.
 */
function StageBones({ label }: { label: string }) {
  return (
    <Skeleton label={label} className={`${styles.workspace} ${styles.stageBones}`}>
      <span className={styles.surfaceBone} aria-hidden="true">
        <Bone height="100%" />
      </span>
      <div className={styles.side} aria-hidden="true">
        <Bone width="70%" height="2.5rem" />
        <Bone width="90%" />
        <Bone width="60%" />
      </div>
    </Skeleton>
  );
}

/**
 * Drawing an outline by hand — the way in when there is no drawing to read,
 * or when the reading missed the shape (design 2026-08-15 §1).
 *
 * The order this screen is built around, and says out loud: **first the form,
 * then the figures.** Pulling a corner or pushing a wall changes what a wall
 * is, and a number typed before that stops describing it — so the numbers come
 * last, and a wall that loses one says so by going dashed.
 *
 * Nothing here computes a length that counts. The board holds coordinates, the
 * fields hold what somebody typed, and the closure, the scale and every
 * finding are worked out by `schema/setting.py` once „Übernehmen" is pressed
 * (A-014, and the working rule: keine Fachaussagen in der Darstellungsschicht).
 */
export function OutlineDraftScreen({
  readingPageUrl,
}: {
  /** The page a reading saw — the dev preview's synthetic sketch; the page route otherwise. */
  readingPageUrl?: string;
} = {}) {
  useDocumentTitle("Umriss");

  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useScreenToast();
  // `/umriss?case=<id>`: the drawing becomes Stand 1 of this Vorgang instead
  // of a new one (R-4, UX gate round 1 #2) — the record's „Umriss zeichnen →".
  const [params, setParams] = useSearchParams();
  const forCase = params.get("case");
  const target = useCase(forCase);
  // `/umriss?case=…&aus=lesung[&kandidat=n]` (plan 4 §5): the outline the
  // reading proposed, to be put right here. Without `aus=lesung` nothing
  // below changes anything.
  const fromReading = forCase !== null && params.get("aus") === "lesung";
  const candidateParam = params.get("kandidat");
  const candidate = candidateParam !== null && /^\d+$/.test(candidateParam) ? Number(candidateParam) : null;
  const reading = useExtraction(fromReading ? forCase : null);
  const readingId = reading.data?.status === "succeeded" ? reading.data.extractionId : null;
  const seed = useQuery({
    queryKey: editorSeedKey(forCase ?? "", readingId ?? "", candidate ?? 0),
    queryFn: () => editorSeed(forCase!, readingId!, candidate ?? 0),
    enabled: fromReading && readingId !== null,
    staleTime: Infinity,
    retry: false,
  });
  const seedKey = `${forCase}|lesung|${readingId}|${candidate ?? "-"}`;
  const seededDraft = useMemo(() => {
    if (!seed.data || forCase === null || readingId === null) return null;
    // Figures a person set on the review screen ride along — the same walls
    // of the same reading, and only for the form the review showed (no
    // `kandidat`): another reading of the form has other walls.
    const review = reading.data ? reviewOf(reading.data) : null;
    const typed = new Map<number, number>();
    if (candidate === null && review !== null && review.walls.length === seed.data.walls.length) {
      const byWall = new Map(loadResolutions(forCase, readingId).map((entry) => [entry.wallId, entry.valueMm]));
      review.walls.forEach((wall, index) => {
        const value = byWall.get(wall.wallId);
        if (value !== undefined) typed.set(index, value);
      });
    }
    return draftFromSeed(seed.data, typed);
  }, [seed.data, forCase, readingId, candidate, reading.data]);
  // The sheet in the seed's millimetres: the page can then lie under the
  // outline 1:1 instead of beside it.
  const sheet = useMemo(() => {
    const review = reading.data ? reviewOf(reading.data) : null;
    const points = review?.candidates[candidate ?? 0]?.points ?? [];
    return seed.data ? sheetSize(seed.data.vertices, points) : null;
  }, [seed.data, reading.data, candidate]);
  const seedProblem = !fromReading
    ? null
    : reading.isError || seed.isError
      ? seed.error instanceof TakeOverConflict
        ? NEWER_READING
        : "Der Umriss aus der Lesung lässt sich nicht laden."
      : reading.data && reading.data.status !== "succeeded"
        ? "Die Lesung ist noch nicht fertig."
        : seed.data && seededDraft === null
          ? "Der Umriss aus der Lesung passt nicht zusammen — zeichne ihn selbst."
          : null;
  // `/umriss?case=…&aus=stand` (spec 2026-10-15 §3, „Form ändern …“): the
  // form of the Vorgang's latest Stand, with every figure and where it came
  // from, to be drawn anew — and written back as a Basisstand, never as
  // Stand 1. A third way in beside the two above; neither of them changes.
  const fromStand = forCase !== null && params.get("aus") === "stand";
  const stand = useQuery({
    queryKey: standSeedKey(forCase ?? ""),
    queryFn: () => standSeed(forCase!),
    enabled: fromStand,
    staleTime: Infinity,
    retry: false,
  });
  // Kept under the Stand it was taken from: coming back to the same Stand
  // finds the drawing under way, a Stand that moved on starts anew.
  const standKey = `${forCase}|stand|${stand.data?.versionNo ?? "-"}`;
  const standDraft = useMemo(() => (stand.data ? draftFromStand(stand.data) : null), [stand.data]);
  // A 404 is the Vorgang's, not the seed's: „gibt es nicht“ below says it.
  const standProblem: "none" | "failed" | "unfit" | null = !fromStand
    ? null
    : stand.isError
      ? stand.error instanceof ProblemError && stand.error.status === 404
        ? null
        : conflictOf(stand.error)?.reasons.includes("no_stand")
          ? "none"
          : "failed"
      : stand.data && standDraft === null
        ? "unfit"
        : null;
  // What the Vorgang allows, once it has answered (spec §4): a form changes
  // until „Angebot raus“, and not on a cancelled Vorgang.
  const editability = fromStand && target.data ? facadeEditability(target.data.status) : null;
  const frozen = editability === "sent" || editability === "cancelled" ? editability : null;
  // A wall whose figure the sketch contradicts (E-64) is drawn here at the
  // figure typed — the board draws it at the sketch's length. Not redrawn
  // (Ruling R-19): said, where the form is being changed. Only from a Stand:
  // the other ways in have no plan to read it off.
  const disputed = useDisputedFindings(fromStand ? forCase : null);
  // The Stand is what this way in needs, not what holds it.
  const standing = forCase !== null && !fromStand && (target.data?.latestVersionNo ?? 0) > 0;
  // Gone only on its 404 — read before the data, which the cache may still
  // hold beside it (final review of Plan D, minor). Any other failure is
  // „not reachable": worth another try, and no reason to send anyone to the
  // register.
  const missing = forCase !== null && target.error?.message === RECORD_NOT_FOUND;
  const unreachable = forCase !== null && !missing && target.isError && target.data === undefined;
  // Whether the Vorgang has its Stand is known only once it has answered;
  // „Übernehmen" waits for that rather than post against a Stand it cannot see.
  const waiting =
    forCase !== null &&
    !missing &&
    !unreachable &&
    (target.data === undefined ||
      (fromReading && seededDraft === null) ||
      (fromStand && standDraft === null && standProblem === null && frozen === null));
  const { maximized, toggle: toggleMaximized, buttonRef: maximizeRef } = useMaximize();
  // The Vorgang's drawing beside the board (UX gate round 3, fix #3):
  // tracing instead of drawing blind. Only shown — nothing is read from it
  // here (E-54), and the page is the one the record already offers. The one
  // the record's „Umriss danach zeichnen →" chose (`?vorlage=<id>`, round-4
  // screen check R4-13), else the first.
  const chosen = params.get("vorlage");
  const documents = [...(target.data?.documents ?? [])].sort((a, b) => a.sortIndex - b.sortIndex);
  const drawing =
    forCase !== null ? (documents.find((document) => document.id === chosen) ?? documents[0] ?? null) : null;
  const [referenceShown, setReferenceShown] = useState(true);
  const [referenceFailed, setReferenceFailed] = useState(false);
  // Where everything stacks (≤ 60 rem), drawing and board share one place and
  // a switch „Vorlage | Brett" says which stands there (UX gate round 4, #4)
  // — the drawing as large as the board, not a thumbnail above it.
  const [stackedView, setStackedView] = useState<"board" | "reference">("board");
  // From a reading the reference is the page the reading saw — the one the
  // outline's corners refer to — not the uploaded file.
  // Under the outline where the sheet is known, beside it where it is not.
  const underlayAvailable = fromReading && sheet !== null;
  const referenceSrc =
    forCase === null
      ? null
      : fromReading
        ? sheet === null
          ? (readingPageUrl ?? extractionPageUrl(forCase, 1))
          : null
        : drawing !== null
          ? casePageUrl(forCase, drawing.id, 1)
          : null;
  const reference = referenceSrc !== null && referenceShown;
  const underlay =
    underlayAvailable && referenceShown && forCase !== null && sheet !== null
      ? { href: readingPageUrl ?? extractionPageUrl(forCase, 1), ...sheet }
      : null;

  /** Another drawing as the reference (UX gate round 4, #4) — kept in the address, as the record's link sets it. */
  function chooseDrawing(id: string): void {
    setReferenceFailed(false);
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("vorlage", id);
        return next;
      },
      { replace: true },
    );
  }

  function showStacked(view: "board" | "reference"): void {
    setStackedView(view);
    if (view === "reference") setReferenceShown(true);
  }

  const history = useOutlineDraft((state) => state.history);
  /** What Wiederholen would put back — read here so its button follows it. */
  const future = useOutlineDraft((state) => state.future);
  /**
   * „Eine Wand · Rechteck“ (Task 17) — only where a new drawing starts, not
   * on a reading's outline or a Stand's form, nor for a Vorgang that has its
   * Stand already (review T17-4). Read off the first step, the one „Von
   * vorn“ returns to; before the store has one, the stand-in's.
   */
  const startShape =
    !fromReading && !fromStand && !standing ? startShapeOf(history[0] ?? FIRST_FRAME) : null;
  /** The choice of start, to put the focus back on its chosen radio. */
  const startGroupRef = useRef<HTMLDivElement>(null);
  /**
   * The question before work is thrown away: a new start (Task 17) or „Von
   * vorn“ (UX-Gutachten 4) — both ask once anything was drawn.
   */
  const [ask, setAsk] = useState<{ kind: "start"; shape: StartShape } | { kind: "restart" } | null>(null);
  /** A shape gesture waiting for its answer — it would take figures off walls (`figuresLost`). */
  const [figureAsk, setFigureAsk] = useState<FigureAsk | null>(null);
  const figureAskCount = useRef(0);
  const undoButtonRef = useRef<HTMLButtonElement>(null);
  const redoButtonRef = useRef<HTMLButtonElement>(null);
  /** What Rückgängig or Wiederholen last did, for the status region — emptied
   * first and filled a moment later, so the same words twice are read twice. */
  const [stepSaid, setStepSaid] = useState("");
  const stepSaidRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (stepSaidRef.current !== null) window.clearTimeout(stepSaidRef.current);
    },
    [],
  );
  const selected = useOutlineDraft((state) => state.selected);
  const selectedCorner = useOutlineDraft((state) => state.selectedCorner);
  /** A wall to put the focus on once the board has drawn it — after „Ecke entfernen“, the merged one. */
  const focusWall = useRef<number | null>(null);
  useEffect(() => {
    const index = focusWall.current;
    if (index === null) return;
    focusWall.current = null;
    document.querySelector<SVGElement>(`[data-role="wall-handle"][data-wall="${index}"]`)?.focus();
  });
  const draftStore = useOutlineDraft;
  /** What is in the Maß field right now — kept as text so a half-typed „10," is not a rejection. */
  const [lengthText, setLengthText] = useState("");
  /** Which wall `lengthText` was last filled for — see the adjustment below. */
  const [textFor, setTextFor] = useState<number | null>(null);
  /** The calibration call in flight, and what it had to say. */
  const [calibrating, setCalibrating] = useState(false);
  const [calibrateNote, setCalibrateNote] = useState<string | null>(null);
  /**
   * „Übernehmen" in flight, and what it had to say — this screen's own state,
   * not the store's `status`/`error`: `handOver` calls `manualOutline`,
   * `createCase` and `postVersion` directly (Task 14), the same reason
   * `calibrate` above never touched the store either.
   */
  const [handingOver, setHandingOver] = useState(false);
  const [handOverError, setHandOverError] = useState<string | null>(null);
  /**
   * A wall length longer than any facade (UX-Gutachten 23.10.2026, Nr. 7),
   * asked about where the figure counts — the field left or Enter, as a
   * chamfer the figure strands is (`settleChamfers`) — and at the latest
   * before „Übernehmen“ writes a Stand. Not on every keystroke: „6“ is „60“
   * for one keystroke, „45“ is „4500“'s way there. Live typing still puts
   * every figure on the wall at once, so the field and the drawing never
   * disagree; the question is what a figure must answer before it goes out.
   * The wall it is about, and the figures; shown only while that wall still
   * carries the figure asked about.
   */
  const [doubt, setDoubt] = useState<{ wall: number; mm: number; suggestionMm: number; focusTick: number } | null>(
    null,
  );
  /**
   * How often the question was asked to take the focus — Enter in the field,
   * „Übernehmen“ held (Task 15c). Leaving the field asks without it: the
   * person went somewhere on purpose, and a question that took the focus
   * there had their typing land in it, an Enter answering it unasked.
   */
  const [doubtFocusTick, setDoubtFocusTick] = useState(0);
  /**
   * Long walls a person kept with „Ja“ — `wallIndex:mm`. Keyed by place and
   * figure, so a renumbered wall or a changed figure asks again: asking once
   * too often is the safe way wrong.
   */
  const [meantLong, setMeantLong] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * The Vorgang `createCase` returned, kept across a failed retry (Fix round
   * 1, Important 2) so a second „Übernehmen" after `postVersion` failed does
   * not create a second Vorgang. Valid until „Von vorn" (`restart()`) starts
   * a new drawing over the old one — cleared there alongside the other two
   * fields below (Fix round 2, Important 2). Its number with it: the toast
   * after a retry names the Vorgang the first press made (Welle 1), and its
   * name stands in the name field, no longer to be changed (Task 14): the
   * retry writes a Stand, it does not rename.
   */
  const [createdCase, setCreatedCase] = useState<{ id: string; caseNo: string; title: string } | null>(null);
  /**
   * „Name des Vorgangs“, as typed (Task 14) — only on the way that makes a
   * new Vorgang, optional: left empty it is „Umriss von Hand“ as before, so
   * the quick way is not a step longer. Trimmed when it goes (`caseTitleFrom`).
   */
  const [caseName, setCaseName] = useState("");
  /** „Übernehmen“, for Enter in the name field while it is held: the button says why. */
  const handOverButton = useRef<HTMLButtonElement>(null);
  /**
   * The answer `manualOutline` settled, and the draft it settled — kept
   * across a failed retry so the same „Übernehmen" does not re-settle a
   * draft nobody touched, but **not** across an edit: `settledFor` is a
   * fingerprint (`draftFingerprint`) of the draft this answer belongs to, and
   * `handOver` below re-settles the moment the draft in front of the person
   * no longer matches it (Fix round 2, Important 2 — a wall length changed
   * between two presses of „Übernehmen" must reach the server, not the
   * previous press's answer).
   */
  const [settledAnswer, setSettledAnswer] = useState<DemoRunResult | null>(null);
  const [settledFor, setSettledFor] = useState<string>("");

  /**
   * Where this drawing is kept in the browser (Welle 1, idea 12) — one place
   * per way in, and for a seeded drawing the Stand or reading it began on —
   * or `null` while a seeded way in has nothing to draw yet.
   */
  const seededReady = seededDraft !== null;
  const standReady = standDraft !== null;
  const keptAt = useMemo<DraftContext | null>(() => {
    if (fromReading) return forCase !== null && seededReady ? { way: "lesung", caseId: forCase, basis: seedKey } : null;
    if (fromStand) return forCase !== null && standReady ? { way: "form", caseId: forCase, basis: standKey } : null;
    return forCase !== null ? { way: "vorgang", caseId: forCase } : { way: "neu" };
  }, [fromReading, fromStand, forCase, seededReady, seedKey, standReady, standKey]);
  const keptKey = keptAt === null ? null : `${draftStorageKey(keptAt)}|${"basis" in keptAt ? keptAt.basis : ""}`;
  /**
   * Whether this browser keeps anything at all — what the foot says. Asked
   * once, when the screen opens: a drawing is a kilobyte or two, and a
   * storage that takes the probe takes the drawing.
   */
  const [keeps] = useState(draftStorageWorks);
  /**
   * What the browser had kept for this way in, asked once per way in
   * (`key`) — `kept: null` where there is nothing (more) to offer.
   */
  const [offer, setOffer] = useState<{ key: string; kept: StoredDraft | null } | null>(null);
  /**
   * That a chamfer went, and why — on screen (`faseNotice`, beside the
   * drawing) and read out (`announcement`, a live region that is always
   * there: one mounted together with its text is not reliably announced).
   */
  const [faseNotice, setFaseNotice] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  /**
   * A chamfer was taken off when the Maß field was left or Enter pressed,
   * and nothing the person did since says they have seen it (review of
   * Task 7, round 2). The usual way to leave the field *is* the press on
   * „Übernehmen“: the mouse-down blurs it, the chamfer goes, and the click
   * would send the cleaned form and leave the screen while the notice is
   * still arriving. So the next „Übernehmen“ or „Kalibrieren“ stops once,
   * with the reason; the press after that goes. Cleared by any step on the
   * draft (`push`), by „Rückgängig“ and „Von vorn“, and by closing the notice.
   */
  const unseenDrop = useRef(false);
  /**
   * The Fase fields, filled afresh for every corner picked (`faseFor`).
   * Angle, and **one** length — the leg or the slant, whichever was typed
   * last (`faseDriver`); the other is worked out and shown, never a second
   * figure to type over. `fasePristine` while the fields still show the
   * chamfer already on the corner: that one stands as it is, and its legs are
   * not re-derived from a rounded angle.
   */
  const [faseFor, setFaseFor] = useState<number | null>(null);
  const [angleText, setAngleText] = useState("45");
  const [faseDriver, setFaseDriver] = useState<"leg" | "slant">("leg");
  const [faseText, setFaseText] = useState("");
  const [fasePristine, setFasePristine] = useState(true);
  /** „Fase setzen“ takes the focus back once „Fase entfernen“, which had it, is gone. */
  const faseSetButton = useRef<HTMLButtonElement>(null);
  const refocusFase = useRef(false);
  useEffect(() => {
    if (!refocusFase.current) return;
    refocusFase.current = false;
    faseSetButton.current?.focus();
  });

  // Reopening the screen keeps what is under way; only the Eingang starts one
  // — and a Vorgang, for the drawing that is its own (`ensureDraftFor`).
  useEffect(() => {
    if (fromReading) {
      if (seededDraft !== null) ensureDraftFor(forCase, { key: seedKey, draft: seededDraft });
      return;
    }
    if (fromStand) {
      if (standDraft !== null) ensureDraftFor(forCase, { key: standKey, draft: standDraft });
      return;
    }
    ensureDraftFor(forCase);
  }, [forCase, fromReading, seededDraft, seedKey, fromStand, standDraft, standKey]);

  // `ensure` runs in an effect, so the very first render has nothing yet. The
  // same rectangle it is about to put in the store stands in for that one
  // frame — a null check threaded through the whole screen would buy a flash
  // of empty board and nothing else.
  // From a reading, the store holds this reading's outline only once the
  // effect above has put it there; for that one render the seed stands in.
  // From a Stand the same, under the Stand's key.
  const seededKey = fromReading ? seedKey : fromStand ? standKey : null;
  const seedOnScreen = fromReading ? seededDraft : fromStand ? standDraft : null;
  const bound = history[0] !== undefined && seededKey !== null && boundTo.get(history[0]) === seededKey;
  const draft: Draft =
    seededKey !== null && !bound ? (seedOnScreen ?? FIRST_FRAME) : (history.at(-1) ?? FIRST_FRAME);

  // Whether the store holds this way in's drawing — the owner `ensureDraftFor`
  // binds it to — and whether anything in it would be lost: a step away from
  // its first one that did not go through „Übernehmen“.
  const ownerKey = fromReading ? seedKey : fromStand ? standKey : forCase;
  const firstStep = history[0];
  const latestStep = history.at(-1);
  // Any other step on the stack — Rückgängig, a typed figure, a button — ends
  // the question: it was about the shape that stood when it was asked.
  if (figureAsk !== null && figureAsk.at !== latestStep) setFigureAsk(null);
  const onPlace = firstStep !== undefined && (boundTo.get(firstStep) ?? null) === ownerKey;
  const unsaved =
    onPlace &&
    latestStep !== undefined &&
    !handedOver.has(latestStep) &&
    draftFingerprint(latestStep) !== draftFingerprint(firstStep);

  // A drawing kept before a reload (idea 12) is offered back where the tab
  // has nothing newer than the way in's first step — asked once per way in,
  // as soon as the store holds its drawing, and never put on the board
  // unasked. Adjusted during the render that notices, like the Maß field
  // below: an effect would come a commit late.
  if (keptAt !== null && keptKey !== null && onPlace && offer?.key !== keptKey) {
    const kept = history.length === 1 ? loadStoredDraft(keptAt) : null;
    setOffer({
      key: keptKey,
      kept:
        kept !== null &&
        draftFingerprint(kept.draft) !== draftFingerprint(firstStep) &&
        !BARE_STARTS.has(draftFingerprint(kept.draft))
          ? kept
          : null,
    });
  }
  const pending = offer !== null && offer.key === keptKey ? offer.kept : null;
  const offering = pending !== null;

  // Every step is written to the browser as it is made — the shape, the
  // figures, the labels, nothing else (`lib/outlineDraftStorage.ts`). Back at
  // the first step (Rückgängig, „Von vorn“) there is nothing left to keep, and
  // the copy goes. Nothing while an earlier drawing is offered: that one stays
  // until somebody chooses, whatever is drawn meanwhile — the offer is
  // decided in the render above, before this runs.
  useEffect(() => {
    if (keptAt === null || offering || !onPlace || firstStep === undefined || latestStep === undefined) return;
    if (handedOver.has(latestStep)) return;
    if (draftFingerprint(latestStep) !== draftFingerprint(firstStep)) saveStoredDraft(keptAt, latestStep);
    else clearStoredDraft(keptAt);
  }, [keptAt, offering, onPlace, firstStep, latestStep]);

  // Leaving the page — reload, closing the tab, another address — asks first
  // while there is a drawing nobody took over. The browser's own question:
  // the page cannot word it. Inside the app nothing is lost; the store and
  // the browser's copy both stay.
  useEffect(() => {
    if (!unsaved) return;
    function ask(event: BeforeUnloadEvent): void {
      event.preventDefault();
      // Older engines (Chrome and Edge before 119) ask only where a value is
      // set, and an empty string is no value to them. The text itself is
      // never shown — the browser words the question.
      event.returnValue = "Der Entwurf ist noch nicht übernommen.";
    }
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [unsaved]);

  // Said under „?“ beside „Übernehmen“ (B-6): kept in this browser. Where it
  // keeps nothing the sentence stands in view under the surface instead (fix
  // round 1 of Task 10), once.
  const keptNote = keeps ? ` ${KEPT_NOTE}` : "";
  // The way in, one sentence over the stage; the rest under „?“ (B-6).
  const { sentence: ledeSentence, rest: ledeRest } = ledeOf(
    fromStand ? "stand" : fromReading ? "reading" : startShape === "wall" ? "wall" : "shape",
    stand.data?.versionNo ?? null,
    standDraft?.walls.some((each) => each.readMm != null) ?? false,
  );
  // The stage's body: the drawing — or, while the form of a Stand or the
  // outline of a reading is on its way, its bones; neither where it failed
  // (the notice above the stage says so).
  const awaited = (fromStand && standDraft === null) || (fromReading && seededDraft === null);
  const loaded = !awaited;
  const loading = !awaited
    ? null
    : fromStand
      ? standProblem === null
        ? STAND_LOADING
        : null
      : seedProblem === null
        ? READING_LOADING
        : null;
  // Not where the drawing could not be taken over anyway.
  const offered = !standing && !missing && frozen === null && standProblem === null ? pending : null;

  /**
   * „Entwurf vom … wiederherstellen“: the kept drawing as one step on top of
   * what is on the board — Rückgängig takes it back, „Von vorn“ still leads
   * to the way in's first step. The focus goes to the drawing's first wall;
   * the button that had it is gone.
   */
  function restoreKept(): void {
    if (pending === null || keptKey === null) return;
    // A standing drag question holds every step (`questionHoldsSteps`): the
    // kept draft stays on offer instead of being consumed under it.
    if (questionHoldsSteps()) return;
    setOffer({ key: keptKey, kept: null });
    // No gesture closes a walk, so a kept ring began as the rectangle — and
    // goes back on one, or „Von vorn“ and the choice of start would say
    // „Eine Wand“ under a ring (Task 17). Only while nothing was drawn since
    // the screen opened (review T17-1): otherwise that would go with the
    // start it stands on, and the ring is one step on top, as promised.
    if (startShape === "wall" && pending.draft.closed && history.length === 1) startOverFor(forCase, "rectangle");
    commit(pending.draft);
    focusWall.current = 0;
  }

  /**
   * „Verwerfen“: the kept drawing goes. What was drawn while the question
   * stood is kept from now on — the effect above writes it once the offer is
   * gone.
   */
  function discardKept(): void {
    if (keptAt === null || keptKey === null) return;
    setOffer({ key: keptKey, kept: null });
    clearStoredDraft(keptAt);
    focusWall.current = 0;
  }

  /**
   * After „Übernehmen“ went through: nothing left to keep or to ask about.
   *
   * On the free way in, the browser's copy goes only where it is the drawing
   * that went through. An earlier one still on offer is not: the offer says
   * „Bis du wählst“, taking over what is on the board is no choice about the
   * one in the box, and that one can still become a Vorgang of its own.
   * Decided by what is kept, not by the offer as this press saw it — a
   * „Verwerfen“ or „Wiederherstellen“ while the request ran changes both.
   * Every other way in has its Stand once this went through, and a copy kept
   * for it could never be taken over again.
   */
  function keptDone(handed: Draft): void {
    handedOver.add(handed);
    if (keptAt === null) return;
    if (keptAt.way === "neu") {
      const kept = loadStoredDraft(keptAt);
      if (kept !== null && draftFingerprint(kept.draft) !== draftFingerprint(handed)) return;
    }
    clearStoredDraft(keptAt);
  }

  // The field follows the selection, wherever the selection came from — a
  // click, or coming back to this screen (the browser's Back from the board)
  // with a wall still picked.
  // Setting it in `selectWall` alone left the field empty on return while the
  // wall it belonged to carried a figure (measured in the browser,
  // 15.08.2026).
  //
  // Adjusted during the render that notices the change, not in an effect:
  // React re-runs this component immediately and nothing in between is ever
  // painted, where an effect would render the stale text once first. The
  // guard is what keeps it from being a loop — and it is deliberately *not*
  // watching `draft`, because typing is what changes the draft, and reacting
  // to that would wipe the field on every keystroke.
  if (textFor !== selected) {
    setTextFor(selected);
    const picked = selected === null ? null : (draft.walls[selected] ?? null);
    setLengthText(
      picked?.lengthMm != null ? formatMetres(picked.lengthMm).replace(" m", "") : "",
    );
  }
  const running = handingOver;
  const blocked = standing || missing || unreachable || waiting || frozen !== null || standProblem !== null;
  // No Stand without a single measure (Denis, 19.10.2026, Vorgang 2026-0009):
  // a form reading taken over without figures stood in the planning with
  // nothing a grating could lie against. The server refuses it as well
  // (`append_version`, 422); here „Übernehmen" waits and says why.
  const unmeasured = draft.walls.every((each) => each.lengthMm === null);
  // „Noch kein Maß" keeps its place once it has stood here (N-5): the first
  // figure typed must not move the board under the pointer. Where it never
  // stood — a drawing that opens with its figures, as from a reading — it
  // keeps none: the head held an empty band of up to 98 px over the board
  // (375 px; UX-Gutachten 2, B-12). Set during the render, as above.
  const [holdStood, setHoldStood] = useState(unmeasured);
  if (unmeasured && !holdStood) setHoldStood(true);
  const held = blocked || unmeasured;
  const wall = selected === null ? null : (draft.walls[selected] ?? null);
  /** The question, on the wall it is about and while that wall still carries the figure asked about. */
  const shownDoubt =
    doubt !== null && doubt.wall === selected && wall !== null && wall.lengthMm === doubt.mm ? doubt : null;
  const cornerCount = draft.closed ? draft.vertices.length - 1 : draft.vertices.length;
  const corner = selectedCorner !== null && selectedCorner < cornerCount ? selectedCorner : null;
  const removal = corner === null ? null : cornerRemoval(draft, corner);

  // ── Fase at the picked corner (design 2026-10-18 §6.5) ────────────────────
  // Only on the hand path and in „Form ändern …“ (§5.1): after a reading the
  // editor is the one the M2 measurements run through, and it stays as it was.
  const faseMode = !fromReading;
  const wallCount = draft.walls.length;
  /** The wall that ends at the picked corner — a chamfer there is keyed by it. */
  const faseWall = corner === null ? null : draft.closed ? (corner - 1 + wallCount) % wallCount : corner - 1;
  /**
   * The two walls a slant at the picked corner joins — where the walk turns
   * there. An open end has one wall, a joint on one line no corner to cut.
   */
  const fasePlace =
    faseMode && removal === "turns" && faseWall !== null && faseWall >= 0
      ? { wall: faseWall, next: draft.closed ? (faseWall + 1) % wallCount : faseWall + 1 }
      : null;
  // Why there is nothing to set here, in the server's words. The start of an
  // open chain is an open end just like its last corner, and `chamferProblem`
  // names that one at the last wall.
  const faseNoCorner =
    !faseMode || corner === null || faseWall === null || fasePlace !== null
      ? null
      : chamferProblem(draft, {
          wallIndex: removal === "end" ? wallCount - 1 : faseWall,
          legAMm: 1,
          legBMm: 1,
        });
  const existing =
    fasePlace === null ? undefined : draft.chamfers?.find((each) => each.wallIndex === fasePlace.wall);
  // Filled afresh for each corner, the way the Maß field follows its wall:
  // during the render that notices the change, not in an effect.
  if (faseFor !== corner) {
    setFaseFor(corner);
    setAngleText(existing ? String(angleOf(existing.legAMm, existing.legBMm)).replace(".", ",") : "45");
    setFaseDriver("leg");
    setFaseText(existing ? String(existing.legAMm) : "");
    setFasePristine(true);
  }
  const angle = parseAngle(angleText);
  const typedFase = parseLengthMm(faseText);
  const legs =
    fasePlace !== null && angle.ok && typedFase.ok
      ? legsFromAngle(angle.deg, faseDriver === "leg" ? { legAMm: typedFase.mm } : { diagonalMm: typedFase.mm })
      : null;
  /** The chamfer „Fase setzen“ would set — the one on the corner while the fields still show it. */
  const wouldBe: DraftChamfer | null =
    fasePlace === null
      ? null
      : fasePristine && existing !== undefined
        ? existing
        : legs === null
          ? null
          : { wallIndex: fasePlace.wall, ...legs };
  const faseRefusal =
    fasePristine || typedFase.ok || typedFase.reason === "empty" ? null : FASE_LENGTH_REFUSALS[typedFase.reason];
  const angleRefusal = angle.ok || angle.empty ? null : ANGLE_REFUSAL;
  // What holds „Fase setzen“: first what the corner lacks whatever the legs
  // (a figure on both walls — probed with the smallest legs there are), then
  // the fields, then the legs themselves.
  const faseReason =
    fasePlace === null
      ? null
      : (chamferProblem(draft, { wallIndex: fasePlace.wall, legAMm: 1, legBMm: 1 }) ??
        (wouldBe === null
          ? FASE_EMPTY
          : (chamferProblem(draft, wouldBe) ??
            (existing !== undefined && existing.legAMm === wouldBe.legAMm && existing.legBMm === wouldBe.legBMm
              ? FASE_STANDS
              : null))));
  /** The draft as „Fase setzen“ would leave it — the echo's figures, and what is numbered how. */
  const preview = wouldBe !== null && chamferProblem(draft, wouldBe) === null ? setChamfer(draft, wouldBe) : null;
  // Numbered as they will be once the slant is set — the wall after it moves
  // on by one — whether or not it can be set yet.
  const numbered: Draft | null =
    fasePlace === null
      ? null
      : {
          ...draft,
          chamfers: [
            ...(draft.chamfers ?? []).filter((each) => each.wallIndex !== fasePlace.wall),
            wouldBe ?? { wallIndex: fasePlace.wall, legAMm: 1, legBMm: 1 },
          ],
        };

  /** Says that a chamfer went: beside the drawing, and to a screen reader. */
  function tell(text: string): void {
    setFaseNotice(text);
    setAnnouncement(text);
  }

  /**
   * A step on the stack and nothing else — for the gestures that say for
   * themselves what they did to a chamfer. A step the person takes after a
   * chamfer went is taken on the form they now see (`unseenDrop`).
   */
  function push(next: Draft, live = false): void {
    if (questionHoldsSteps()) return;
    unseenDrop.current = false;
    draftStore.getState().push(next, live);
  }

  /**
   * Whether a drag's preview stands on the stack with its question open. It
   * is the one unanswered state that already differs from what the person
   * had, and **Abbrechen is its default**: so no other step lands on top of
   * it (the next gesture would start from the preview, where `figuresLost` can
   * no longer see the loss). The press goes to the question instead; Rückgängig
   * and Wiederholen stay as they are. A key press or a button held for its
   * question (`applied: false`) is not such a state — nothing was pushed, and
   * any other step simply ends it.
   */
  function questionHoldsSteps(): boolean {
    if (figureAsk === null || !figureAsk.applied) return false;
    focusQuestion();
    return true;
  }

  /** Whether a dropped chamfer is still unseen — and from now on, seen: the hold it causes is the showing. */
  function takeUnseenDrop(): boolean {
    const unseen = unseenDrop.current;
    unseenDrop.current = false;
    return unseen;
  }

  /**
   * Every gesture lands here, and every one of them is a step on the stack.
   *
   * A drag produces a frame per pointer move, and each frame is computed from
   * the shape the drag started on (`OutlineDraft`'s own `base`), so pushing
   * each frame would fill the stack with a hundred copies of one gesture.
   * `replace` is how a gesture in flight overwrites its own last frame; it
   * ends with one entry, and Rückgängig undoes the gesture rather than a pixel
   * of it.
   *
   * A change of form that takes chamfers with it says so — once: the frames
   * after the first start from a draft that has none left to lose. `from` is
   * the draft the gesture began on, where that is not the one on screen.
   */
  function commit(next: Draft, live = false, from: Draft = draft): void {
    if (questionHoldsSteps()) return;
    if ((next.chamfers?.length ?? 0) < (from.chamfers?.length ?? 0)) tell(FASEN_GONE);
    push(next, live);
  }

  /**
   * What the board's gestures land on (`OutlineDraft`'s `onDraft`). A key press
   * comes with `taken` and is a whole step, asked about before it is taken
   * (`askOrCommit`). The frames of a drag are never held; they are the
   * preview, and `askAboutDrag` asks once the pointer lets go.
   */
  function boardStep(next: Draft, live: boolean, taken?: () => void): void {
    if (taken === undefined) commit(next, live);
    else askOrCommit(next, taken);
  }

  /**
   * A whole step that may take figures off walls: if it would, it is held for
   * the question instead of pushed — the shape on screen does not change until
   * the person says so. `then` follows once it counts.
   */
  function askOrCommit(next: Draft, then?: () => void): void {
    if (questionHoldsSteps()) return;
    const lost = figuresLost(draft, next);
    if (lost.length === 0) {
      commit(next);
      then?.();
      return;
    }
    figureAskCount.current += 1;
    const active = document.activeElement;
    setFigureAsk({
      id: figureAskCount.current,
      base: draft,
      next,
      lost,
      applied: false,
      taken: then ?? null,
      opener: active instanceof HTMLButtonElement ? active : null,
      at: draftStore.getState().current() ?? undefined,
      wall: selected,
    });
  }

  /** A drag ended; `base` is the shape it began on and the stack holds where it got to. */
  function askAboutDrag(base: Draft): void {
    // A drag the open question held off moved nothing (`questionHoldsSteps`).
    if (figureAsk?.applied) return;
    const reached = draftStore.getState().current() ?? draft;
    const lost = figuresLost(base, reached);
    if (lost.length === 0) return;
    figureAskCount.current += 1;
    setFigureAsk({
      id: figureAskCount.current,
      base,
      next: reached,
      lost,
      applied: true,
      taken: null,
      opener: null,
      at: reached,
      wall: selected,
    });
  }

  /** „Abbrechen“: the figures stay. A drag's preview goes back the way it came, as one step that changes nothing. */
  function keepFigures(): void {
    if (figureAsk === null) return;
    const { base, next, applied, wall, opener } = figureAsk;
    setFigureAsk(null);
    if (applied) {
      // Straight on the store: `push` would hold this very step for the question.
      unseenDrop.current = false;
      draftStore.getState().push(base, true);
      // The preview took chamfers off and said so; they are back.
      if (faseNotice === FASEN_GONE && (base.chamfers?.length ?? 0) > (next.chamfers?.length ?? 0)) {
        setFaseNotice(null);
      }
      // A merge in the preview renumbered the walls; there is no wall to hold on to then.
      if (base.walls.length !== next.walls.length) {
        selectWall(null);
        return;
      }
    }
    if (opener?.isConnected) opener.focus();
    else focusWall.current = wall;
  }

  /** „Trotzdem verschieben“: the gesture counts, and the walls lose their figures. */
  function dropFigures(): void {
    if (figureAsk === null) return;
    const { next, applied, taken, opener } = figureAsk;
    setFigureAsk(null);
    if (!applied) {
      commit(next);
      taken?.();
    }
    if (opener?.isConnected) opener.focus();
    else focusWall.current = draftStore.getState().selected;
  }

  /** Puts the focus on the open question — where a held press is sent. */
  function focusQuestion(): void {
    document.querySelector<HTMLButtonElement>('[data-role="figure-loss-ask"] button')?.focus();
  }

  function selectWall(index: number | null): void {
    draftStore.getState().select(index);
  }

  /**
   * A typed figure or a label, as it is typed — every keystroke a step.
   *
   * **No chamfer is checked here.** A figure on its way passes through
   * figures nobody meant — „2500“ is „250“ for one keystroke, a cleared field
   * is empty before the new figure goes in — and a chamfer taken off there
   * would not come back when the figure is whole again (review of Task 7). A
   * chamfer the figure strands stays in the draft until the figure counts as
   * typed: when the field is left or Enter is pressed (`settleChamfers`), and
   * in any case before anything is sent.
   */
  function setWall(index: number, change: Partial<{ lengthMm: number | null; label: string | null }>): void {
    push({
      ...draft,
      walls: draft.walls.map((each, at) => (at === index ? { ...each, ...change } : each)),
    });
  }

  /** The question a wall's figure would ask, or `null` — none, or kept with „Ja“. */
  function doubtOf(index: number): { wall: number; mm: number; suggestionMm: number; focusTick: number } | null {
    const mm = draft.walls[index]?.lengthMm ?? null;
    if (mm === null || meantLong.has(`${index}:${mm}`)) return null;
    const suggestionMm = wallLengthDoubt(mm);
    return suggestionMm === null ? null : { wall: index, mm, suggestionMm, focusTick: 0 };
  }

  /**
   * The field was left or Enter pressed: the figure counts, and a long one
   * asks — taking the focus only where `focus` says so (Enter, a held
   * „Übernehmen“), never on leaving the field.
   */
  function askAboutLength(index: number, focus: boolean): void {
    const asked = doubtOf(index);
    if (asked === null) return;
    const standing = doubt !== null && doubt.wall === asked.wall && doubt.mm === asked.mm;
    // Once: the field's blur after an Enter must not put the same question
    // up a second time — nor take the focus back from it.
    if (standing && !focus) return;
    const tick = focus ? doubtFocusTick + 1 : 0;
    if (focus) setDoubtFocusTick(tick);
    setDoubt({ ...asked, focusTick: tick });
  }

  /** The answer: the figure offered goes on the wall, the one typed is kept — back into the field. */
  function answerLength(mm: number): void {
    if (doubt === null || questionHoldsSteps()) return;
    const { wall: index, mm: asked } = doubt;
    setDoubt(null);
    if (mm === asked) {
      setMeantLong((kept) => new Set(kept).add(`${index}:${mm}`));
    } else {
      setWall(index, { lengthMm: mm });
      setLengthText(formatMetres(mm).replace(" m", ""));
    }
    setHandOverError(null);
    document.getElementById("wall-length")?.focus();
  }

  /** Escape in the question: no answer — it comes again when the figure counts. */
  function dismissLength(): void {
    setDoubt(null);
    document.getElementById("wall-length")?.focus();
  }

  /**
   * Takes off the chamfers the typed figures strand — cleared, or too short
   * for the legs on them — and says which and why (design §8: „sichtbar und
   * mit Grund, nie still“). One step on the stack; Rückgängig brings them back
   * with the figure. The draft without them, or `null` where every chamfer
   * still stands.
   *
   * `remember` where the field was left (blur, Enter): the next hand-over or
   * calibration then stops once, so the notice is read before anything goes
   * out (`unseenDrop`).
   */
  function settleChamfers(remember = false): Draft | null {
    if (questionHoldsSteps()) return null;
    const { draft: standing, dropped } = dropStrandedChamfers(draft);
    if (dropped.length === 0) return null;
    push(standing);
    tell(
      dropped
        .map(({ chamfer, reason }) => `Fase an Ecke ${chamferCorner(draft, chamfer.wallIndex)} entfernt — ${reason}`)
        .join(" "),
    );
    if (remember) unseenDrop.current = true;
    return standing;
  }

  /** „Fase setzen“ — one step on the stack, like every other gesture here. */
  function setFase(): void {
    if (corner === null || wouldBe === null || faseReason !== null || running || questionHoldsSteps()) return;
    push(setChamfer(draft, wouldBe));
    setFaseNotice(null);
    setAnnouncement(`Fase an Ecke ${corner + 1} gesetzt.`);
  }

  /** „Fase entfernen“ — one step; the fields keep their figures, so the same chamfer is one press away. */
  function removeFase(): void {
    if (corner === null || fasePlace === null || existing === undefined || running || questionHoldsSteps()) return;
    push(removeChamfer(draft, fasePlace.wall));
    setAnnouncement(`Fase an Ecke ${corner + 1} entfernt.`);
    refocusFase.current = true;
  }

  // The same reading the review screen's wall field gives a figure
  // (`lib/length.ts`): „6,00", „6 m" and „6000" are all six metres. The field
  // used to take metres only, and „6000" — how a drawing writes it — was
  // refused as „Kein Maß zwischen 0,10 m und 100 m" (Generalprobe 15.10.2026).
  const typedLength = parseLengthMm(lengthText);
  const lengthRefusal =
    !typedLength.ok && typedLength.reason !== "empty" ? LENGTH_REFUSALS[typedLength.reason] : null;
  const lengthRefused = lengthRefusal !== null;
  const measured = draft.walls.filter((each) => each.lengthMm !== null).length;

  /**
   * Align the sketch to the typed measures — the button G-6 sketched out.
   *
   * The solver already knows how: `settle` walks direction x settled length
   * and hands the finished nodes back in the answer. So this calls the
   * endpoint the hand-over uses and writes `nodes` into the draft's vertices —
   * **and nothing else**: the walls keep their typed measures and their names,
   * so nothing a person stated is touched by a geometry refresh.
   *
   * `manualOutline` directly, never `startManual`: that one sets the global
   * run state and locks this toolbar — a drawing aid that hijacked the run
   * would make /schema show a run nobody finished.
   *
   * On `facade_closure_conflict` nothing moves (E-16); the finding is shown
   * here, where the measures that contradict each other can be fixed.
   */
  async function calibrate(): Promise<void> {
    // The press that left the Maß field took a chamfer off: stop once, so
    // the notice is read before the drawing moves (`unseenDrop`).
    if (takeUnseenDrop()) {
      setCalibrateNote(STRANDED_CALIBRATE_HOLD);
      return;
    }
    setCalibrating(true);
    setCalibrateNote(null);
    // A figure still in its field counts from here on: a chamfer it strands
    // goes now, said with its reason, not later as „die Form hat sich
    // geändert“ — and this does not wait for the field to be left.
    const base = settleChamfers() ?? draft;
    try {
      // No chamfers (design 2026-10-18): the answer's outline would show each
      // as a wall of its own, and its nodes would no longer be this draft's
      // corners — the check below would throw the calibration away.
      const answer = await manualOutline({
        vertices: base.vertices.map((vertex) => ({ xMm: vertex.xMm, yMm: vertex.yMm })),
        closed: base.closed,
        walls: base.walls.map((each) => ({ lengthMm: each.lengthMm, label: each.label })),
      });
      const conflict = answer.outlineFindings.find(
        (finding) => finding.rule === "facade_closure_conflict",
      );
      if (conflict) {
        setCalibrateNote(conflict.message);
        return;
      }
      const nodes = answer.outlines.find((outline) => outline.chosen)?.nodes;
      if (!nodes || nodes.length !== base.vertices.length) return;
      // The chamfers stay: nothing was sent of them, so the answer's nodes
      // are the draft's own corners, settled — and a chamfer that no longer
      // stands on them goes, said like any change of form.
      commit(
        dropStrandedChamfers({
          ...base,
          vertices: nodes.map((node) => ({ xMm: node.xMm, yMm: node.yMm })),
        }).draft,
        false,
        base,
      );
    } catch (cause: unknown) {
      setCalibrateNote(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setCalibrating(false);
    }
  }

  /**
   * Settles the drawing, then writes it down as a real Vorgang (Task 14,
   * design §9): both entry paths create the case first, then write version 1
   * — here, `trigger = manual_edit`. `manualOutline` directly and not
   * `startManual`: that one sets the global run state and locks the toolbar
   * (see `calibrate`), and this screen has its own state for the hand-over.
   * The Stand goes to the server, and the Vorgang's board reads it back from
   * there.
   *
   * Unconditional, exactly as the previous `startManual`-based version was:
   * a closure conflict is a finding on the answer, not a failure of this
   * call, and the board is where a person reads it — this screen never
   * inspected `outlineFindings` before handing over, and still does not.
   */
  async function handOver(): Promise<void> {
    if (running || held) return;
    // A drag's preview is on the stack while its question stands, and
    // „Übernehmen“ would write it unasked: the press goes to the question.
    if (figureAsk !== null) {
      focusQuestion();
      return;
    }
    // A chamfer a typed figure strands never goes out — and this does not
    // rely on the Maß field having been left first (focus may still be in
    // it). It is taken off, said with its reason, and „Übernehmen“ stops
    // here: the form in front of the person changed, so the next press is
    // theirs to make on what they now see. The same when the press itself
    // left the field and its blur took the chamfer off a moment ago
    // (`unseenDrop`) — the usual order of a mouse click.
    if (settleChamfers() !== null || takeUnseenDrop()) {
      setHandOverError(STRANDED_HOLD);
      return;
    }
    // A wall longer than any facade goes out only answered (UX-Gutachten
    // Nr. 7) — however the figure got there: typed and left by this very
    // press, kept in the browser, or read. The question goes to its wall.
    const long = draft.walls.map((_, index) => doubtOf(index)).find((each) => each !== null) ?? null;
    if (long !== null) {
      selectWall(long.wall);
      // The question takes the focus here: the press is what it holds.
      const tick = doubtFocusTick + 1;
      setDoubtFocusTick(tick);
      setDoubt({ ...long, focusTick: tick });
      setHandOverError(longWallHold(displayWallNumber(draft, long.wall), long.mm));
      return;
    }
    setHandingOver(true);
    setHandOverError(null);
    if (fromStand && forCase !== null && stand.data) {
      await handOverStand(forCase, stand.data.versionNo);
      return;
    }
    if (fromReading && forCase !== null && readingId !== null) {
      await handOverReading(forCase, readingId);
      return;
    }
    try {
      // Reused on a retry of the *same* draft, re-settled the moment the
      // draft moved on since (Fix round 2, Important 2) — `settledFor` is
      // the fingerprint the cached answer belongs to, not merely "some
      // answer exists".
      const fingerprint = draftFingerprint(draft);
      const answer =
        settledAnswer !== null && settledFor === fingerprint
          ? settledAnswer
          : await manualOutline({
              vertices: draft.vertices.map((vertex) => ({ xMm: vertex.xMm, yMm: vertex.yMm })),
              closed: draft.closed,
              walls: draft.walls.map((each) => ({ lengthMm: each.lengthMm, label: each.label })),
              // The answer's outline shows each one cut, and its document
              // carries them into Stand 1 (design 2026-10-18 §6.2).
              chamfers: wireChamfers(draft),
            });
      if (settledFor !== fingerprint) {
        setSettledAnswer(answer);
        setSettledFor(fingerprint);
      }

      // A Vorgang that is already there takes the drawing as its Stand 1
      // (R-4): no second Vorgang, its own grating type untouched — the
      // version route writes a Stand and moves the stage, nothing else. On
      // to its board, which reads the Stand back from the server.
      if (forCase !== null) {
        const written = await postVersion(forCase, {
          expectedVersionNo: 0,
          ops: [],
          document: answer.documentJson ?? null,
          planningState: EMPTY_STATE,
          changeSummary: "Umriss übernommen",
        });
        keptDone(draft);
        // In the cache before the board's frame reads it, or it draws the
        // tabs of a Vorgang without a Stand first (R-6).
        noteStandWritten(client, forCase, written.versionNo);
        void client.invalidateQueries({ queryKey: CASE_KEY(forCase) });
        void client.invalidateQueries({ queryKey: CASES_KEY });
        // Said on the board it lands on (round-2 screen check, R2-6): the
        // drawing is gone from view, and nothing there told that it was kept.
        const title = target.data?.title;
        navigate(`/vorgang/${forCase}/planung`, {
          state: {
            notice:
              title !== undefined
                ? `Umriss als Stand 1 von „${title}“ gespeichert.`
                : "Umriss als Stand 1 gespeichert.",
          },
        });
        return;
      }

      // The case, unlike the answer, is kept whichever draft this is for —
      // it carries no version yet, and version 1 is written below with
      // whatever `answer` just resolved to (Fix round 1's own reasoning:
      // the case is not what needs to match the draft, the document does).
      let made = createdCase;
      if (made === null) {
        // The name as it goes out, kept with the Vorgang: the server stores
        // it trimmed (`workflow.create_case`), which `caseTitleFrom` did already.
        const title = caseTitleFrom(caseName);
        const created = await createCase({
          title,
          sourceChannel: "manual",
          // Not `null` (§15.14, 16.09.2026). A Vorgang without a type
          // answered 422 on every reload, and this screen has no field to
          // put one in — so it states the default the server would fill in
          // anyway, rather than leaving a hole and hoping.
          gratingType: "pressrost",
        });
        made = { id: created.id, caseNo: created.caseNo, title };
        setCreatedCase(made);
      }

      await postVersion(made.id, {
        expectedVersionNo: 0,
        ops: [],
        document: answer.documentJson ?? null,
        planningState: EMPTY_STATE,
        changeSummary: "Umriss übernommen",
      });
      keptDone(draft);
      // Stand 1 is written, so the Vorgang's own board is where this lands,
      // exactly as for a drawing made for a Vorgang that was already there
      // (design 2026-10-22 §7). Not `/schema`: that page is the stateless
      // example, it has no frame around it, and nothing there reads the
      // server's Stand back — so the demo store is no longer fed here.
      // Nothing is cached of a Vorgang made a moment ago, so there is no
      // Stand to raise (`noteStandWritten`): its frame waits for the first
      // answer with a bone, the one place it knows nothing yet (R-6).
      void client.invalidateQueries({ queryKey: CASE_KEY(made.id) });
      void client.invalidateQueries({ queryKey: CASES_KEY });
      // Nothing on the board says a Vorgang was made (Welle 1, idea 23): the
      // shell's toast does, on the page the person lands on — and it is the
      // one that says it, so it is heard; a notice on the board would say it
      // twice. No „Rückgängig": a Vorgang is cancelled with a reason, in its
      // record, not undone.
      // A name somebody gave is said back; the default would only repeat
      // what every hand-drawn Vorgang is called.
      const named = made.title !== MANUAL_CASE_TITLE ? ` „${made.title}“` : "";
      toast.show({ text: `Vorgang ${made.caseNo}${named} angelegt — Umriss als Stand 1 gespeichert.` });
      navigate(`/vorgang/${made.id}/planung`);
    } catch (cause) {
      // `handOverError` below carries the message; the drawing stays exactly
      // as it is.
      if (cause instanceof StaleVersionError && forCase !== null) {
        // Somebody wrote the first Stand meanwhile (a reading taken over, a
        // second tab): read the Vorgang again, so the note at the top says so.
        void client.invalidateQueries({ queryKey: CASE_KEY(forCase) });
        setHandOverError(STANDING);
        return;
      }
      setHandOverError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setHandingOver(false);
    }
  }

  /**
   * The outline from a reading, put right here, becomes Stand 1 (plan 4 §4,
   * `outline/from-editor`). Only what a person may stand for goes out — a
   * figure is a figure somebody kept, whether the reading found it first or
   * not (D-1); the mark „gelesen“ stays behind on this screen.
   */
  async function handOverReading(caseId: string, extractionId: string): Promise<void> {
    try {
      const written = await fromEditor(caseId, {
        extractionId,
        expectedVersionNo: 0,
        closed: draft.closed,
        vertices: walkVertices(draft),
        walls: draft.walls.map((each) => ({ lengthMm: each.lengthMm, label: each.label })),
      });
      clearResolutions(caseId, extractionId);
      keptDone(draft);
      noteStandWritten(client, caseId, written.versionNo);
      void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      void client.invalidateQueries({ queryKey: CASES_KEY });
      navigate(`/vorgang/${caseId}/planung`, { state: { notice: "Umriss als Stand 1 gespeichert." } });
    } catch (cause) {
      if (cause instanceof TakeOverConflict) {
        if (cause.kind !== "stale") void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        setHandOverError(cause.kind === "stale" ? NEWER_READING : STANDING);
      } else if (cause instanceof TakeOverRefused) {
        setHandOverError(cause.detail);
      } else {
        setHandOverError(cause instanceof Error ? cause.message : String(cause));
      }
    } finally {
      setHandingOver(false);
    }
  }

  /**
   * „Übernehmen“ after „Form ändern …“ (spec 2026-10-15 §3): the edited form
   * becomes a Basisstand of this Vorgang — `POST …/outline/basis`, and only
   * that, never Stand 1's routes. Every wall says which of the Stand's walls
   * it started from (`fromWallId`, on the wall object); whether a figure
   * keeps its source is the server's decision. Then to the board, empty, with
   * „Form geändert — neu belegen“ (`?form=neu`).
   *
   * The board's plan is dropped from the cache rather than marked stale: a
   * stale answer would be shown first, the old gratings on the new form,
   * until the new one arrived.
   */
  async function handOverStand(caseId: string, versionNo: number): Promise<void> {
    try {
      const written = await postBasis(caseId, {
        expectedVersionNo: versionNo,
        closed: draft.closed,
        vertices: walkVertices(draft),
        walls: draft.walls.map((each) => ({
          lengthMm: each.lengthMm,
          label: each.label,
          fromWallId: each.fromWallId ?? null,
        })),
        // The Stand's chamfers came with its form and go back with it —
        // the ones kept, changed or set here (design 2026-10-18 §6.1).
        chamfers: wireChamfers(draft),
      });
      keptDone(draft);
      client.removeQueries({ queryKey: [...CASE_KEY(caseId), "plan"] });
      // The seed described the Stand this one replaces: a way back to the
      // editor must ask the server again, not offer it with a live
      // „Übernehmen“ — the dialog's confirm does the same.
      client.removeQueries({ queryKey: standSeedKey(caseId) });
      noteStandWritten(client, caseId, written.versionNo);
      void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      void client.invalidateQueries({ queryKey: CASES_KEY });
      navigate(formChangedPath(caseId));
    } catch (cause) {
      const conflict = conflictOf(cause);
      if (refusedAsCancelled(cause)) {
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        setHandOverError(FACADE_CANCELLED);
      } else if (conflict !== null && conflict.latestVersionNo !== null) {
        // The next „Form ändern …“ reads the Stand afresh; this drawing stays
        // as it is, so nothing is lost before the person decides.
        void client.invalidateQueries({ queryKey: standSeedKey(caseId), refetchType: "none" });
        setHandOverError(STAND_MOVED);
      } else if (cause instanceof ProblemError) {
        // A sent Vorgang, say: read it again, so the note at the top says so.
        if (cause.status === 409) void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        setHandOverError(cause.userMessage);
      } else {
        setHandOverError(cause instanceof Error ? cause.message : String(cause));
      }
    } finally {
      setHandingOver(false);
    }
  }

  /**
   * „Abbrechen“ — the Vorgang stays as it is, and the drawing goes back to
   * the Stand's form (its first step, still bound to the Stand): coming back
   * through „Form ändern …“ does not find a half-done change nobody kept —
   * nor does a reload, so the browser's copy goes with it.
   */
  function leaveStand(caseId: string): void {
    if (keptAt !== null) clearStoredDraft(keptAt);
    draftStore.getState().restart();
    stepBack(navigate, caseId);
  }

  // Rückgängig and Wiederholen — the buttons and ⌘Z, ⇧⌘Z, Strg+Y alike. The
  // keys do exactly what the buttons do, and nothing while a button is grey
  // or not on screen at all: while a Stand's or a reading's form is on its
  // way the store may still hold a drawing from before, which the screen
  // does not show (`bound`), and a step taken there would surface only
  // when that drawing came back.
  const boardShown = !(fromStand && standDraft === null) && !(fromReading && seededDraft === null);
  const storeOnScreen = seededKey === null || bound;
  const canTakeBack = boardShown && storeOnScreen && history.length >= 2 && !running;
  const canPutBack = boardShown && storeOnScreen && future.length > 0 && !running;

  // Each one keeps the keyboard's place — a button gone grey drops the focus
  // to <body>, and so does a wall the step took away — and says what it did:
  // the keys change the board without moving the focus.
  function focusAfterStep(lost: Element): HTMLElement | SVGElement | null {
    const other =
      lost === undoButtonRef.current ? redoButtonRef.current : lost === redoButtonRef.current ? undoButtonRef.current : null;
    if (other !== null && !other.disabled) return other;
    const handles = document.querySelectorAll<SVGElement>('[data-role="wall-handle"]');
    const at = Number(lost.getAttribute("data-wall"));
    return handles[Number.isInteger(at) ? Math.min(Math.max(at, 0), handles.length - 1) : 0] ?? null;
  }

  function sayStep(sentence: string): void {
    if (stepSaidRef.current !== null) window.clearTimeout(stepSaidRef.current);
    setStepSaid("");
    stepSaidRef.current = window.setTimeout(() => {
      stepSaidRef.current = null;
      setStepSaid(sentence);
    }, 50);
  }

  function takeBack(): void {
    keepingFocus(() => {
      const before = draftStore.getState().current();
      draftStore.getState().undo();
      // A chamfer the step took or brought back: the note would be stale.
      setFaseNotice(null);
      unseenDrop.current = false;
      const after = draftStore.getState().current();
      if (before === null || after === null || after === before) return;
      const words = describeDraftStep(after, before);
      sayStep(words === null ? "Rückgängig gemacht." : `Rückgängig gemacht: ${words}`);
    }, focusAfterStep);
  }

  function putBack(): void {
    keepingFocus(() => {
      const before = draftStore.getState().current();
      draftStore.getState().redo();
      // A chamfer the step took or brought back: the note would be stale.
      setFaseNotice(null);
      unseenDrop.current = false;
      const after = draftStore.getState().current();
      if (before === null || after === null || after === before) return;
      const words = describeDraftStep(before, after);
      sayStep(words === null ? "Wiederholt." : `Wiederholt: ${words}`);
    }, focusAfterStep);
  }

  useUndoKeys({ canUndo: canTakeBack, canRedo: canPutBack, onUndo: takeBack, onRedo: putBack });
  const takeBackLabel = history.length >= 2 ? describeDraftStep(history.at(-2)!, history.at(-1)!) : null;
  const putBackLabel = future.length > 0 && history.length > 0 ? describeDraftStep(history.at(-1)!, future.at(-1)!) : null;

  /**
   * What belongs to the drawing thrown away by „Von vorn“ or a new start.
   * The cached answer, case and fingerprint all belong to it (Fix round 2,
   * Important 2) — none of the three describe whatever is drawn next; nor
   * does a long wall kept with „Ja“, or a chamfer's notice.
   */
  function forgetDrawing(): void {
    setFaseNotice(null);
    unseenDrop.current = false;
    setSettledAnswer(null);
    setSettledFor("");
    setCreatedCase(null);
    setMeantLong(new Set());
    setDoubt(null);
  }

  /**
   * Something a new start would throw away: any step away from the start —
   * a figure, a label, a chamfer, and a drawn form too (review T17-2: three
   * walls hung on by hand went without a word, and Rückgängig could not
   * bring them back).
   */
  const anythingToLose = history.length > 1;
  /** Where „Von vorn“ goes back to, said in its question. */
  const restartTarget = fromReading
    ? "Zurück zum Umriss aus der Lesung."
    : fromStand
      ? "Zurück zur Form aus dem Stand."
      : startShape === "wall"
        ? "Zurück zu einer Wand ohne Maß."
        : startShape === "rectangle"
          ? "Zurück zum Rechteck ohne Maß."
          : "Zurück zum Anfang dieser Zeichnung.";
  /** Said once „Von vorn“ went (final review, finding 8): the focus on the first wall names a wall, not that all went. */
  const restartSaid = fromReading
    ? "Von vorn begonnen — wieder der Umriss aus der Lesung."
    : fromStand
      ? "Von vorn begonnen — wieder die Form aus dem Stand."
      : startShape === "wall"
        ? "Von vorn begonnen — wieder eine Wand ohne Maß."
        : startShape === "rectangle"
          ? "Von vorn begonnen — wieder das Rechteck ohne Maß."
          : "Von vorn begonnen.";

  /**
   * The other start (Task 17): at once on an untouched start, after a
   * question once anything was done. Like „Von vorn“, it is a new first
   * step, not a step Rückgängig takes back.
   */
  function chooseStart(shape: StartShape): void {
    if (shape === startShape || running) return;
    if (anythingToLose) setAsk({ kind: "start", shape });
    else startOver(shape);
  }

  /**
   * „Von vorn“ (UX-Gutachten 4): it threw the drawing away at a press, and
   * Rückgängig could not bring it back. Now it asks, like a new start, once
   * anything was drawn — and with nothing drawn it is held anyway.
   */
  function startAgain(): void {
    if (running) return;
    if (anythingToLose) setAsk({ kind: "restart" });
    else restartDrawing();
  }

  function restartDrawing(): void {
    setAsk(null);
    draftStore.getState().restart();
    forgetDrawing();
    sayStep(restartSaid);
  }

  /**
   * After the answer „Von vorn“ the button is held, and the dialog's way back
   * to it ends on the page: the drawing's first wall takes the focus, as
   * after „Wiederherstellen“. After „Abbrechen“ the button has it already.
   */
  function focusAfterRestart(): void {
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    document.querySelector<SVGElement>('[data-role="wall-handle"][data-wall="0"]')?.focus();
  }

  /**
   * Once the question is gone, the focus on the start that is chosen — after
   * „Abbrechen“ or Escape the dialog hands it back to the radio that asked,
   * which is not the chosen one, and is no tab stop (review T17-3).
   */
  function focusChosenStart(): void {
    startGroupRef.current?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus();
  }

  function startOver(shape: StartShape): void {
    setAsk(null);
    startOverFor(forCase, shape);
    forgetDrawing();
    sayStep(STARTED[shape]);
  }

  const tools = (
    <div className={styles.tools}>
      {/* The tooltip names what the press takes back and the keys that do
          the same; the button keeps its one trained word. */}
      <Tooltip
        text={takeBackLabel !== null && canTakeBack ? `Rückgängig: ${takeBackLabel}` : "Rückgängig"}
        keys={undoKeys().undo}
      >
        <Button ref={undoButtonRef} onClick={takeBack} disabled={!canTakeBack}>
          Rückgängig
        </Button>
      </Tooltip>
      <Tooltip
        text={putBackLabel !== null && canPutBack ? `Wiederholen: ${putBackLabel}` : "Wiederholen"}
        keys={undoKeys().redo}
      >
        <Button ref={redoButtonRef} onClick={putBack} disabled={!canPutBack}>
          Wiederholen
        </Button>
      </Tooltip>
      <Button variant="quiet" onClick={startAgain} disabled={history.length < 2 || running}>
        Von vorn
      </Button>
      <Button
        onClick={() => void calibrate()}
        disabled={measured === 0 || calibrating || running}
      >
        {calibrating ? "Wird kalibriert …" : "Kalibrieren"}
      </Button>
      <span className={styles.hint}>
        Richtet die Skizze auf die getippten Maße aus. Unbemaßte Wände behalten ihre
        gezeichnete Länge.
      </span>
    </div>
  );

  /** Enter in a Fase field sets the chamfer, as „Fase setzen“ does. */
  function faseKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== "Enter") return;
    event.preventDefault();
    setFase();
  }

  // „Fase“ under the corner's own actions (design 2026-10-18 §6.5): the angle
  // and one length, what they make, and the one button that sets it.
  const faseSection =
    fasePlace === null && faseNoCorner === null ? null : (
      <div className={styles.fase} role="group" aria-labelledby={FASE_HEAD}>
        <p id={FASE_HEAD} className={`label ${styles.faseHead}`}>
          Fase
        </p>
        {fasePlace === null || numbered === null ? (
          <span className={styles.hint}>{faseNoCorner}</span>
        ) : (
          <>
            <div className={styles.field}>
              <label className={`label ${styles.fieldLabel}`} htmlFor="fase-angle">
                Winkel
              </label>
              <input
                id="fase-angle"
                className={`${styles.input} ${styles.inputNum} ${angleRefusal !== null ? styles.inputBad : ""}`}
                inputMode="decimal"
                autoComplete="off"
                aria-describedby="fase-angle-hint"
                aria-invalid={angleRefusal !== null || undefined}
                value={angleText}
                onChange={(event) => {
                  setAngleText(event.target.value);
                  setFasePristine(false);
                }}
                onKeyDown={faseKey}
              />
              <span
                id="fase-angle-hint"
                className={`${styles.hint} ${angleRefusal !== null ? styles.hintBad : ""}`}
              >
                {angleRefusal ?? (
                  <>
                    Grad, 1 bis 89 — zwischen Wand <span className="tabular">{displayWallNumber(numbered, fasePlace.wall)}</span>{" "}
                    und der Schräge
                  </>
                )}
              </span>
            </div>

            <div className={styles.field}>
              <label className={`label ${styles.fieldLabel}`} htmlFor="fase-leg">
                Schenkel an Wand {displayWallNumber(numbered, fasePlace.wall)}
              </label>
              <input
                id="fase-leg"
                className={`${styles.input} ${styles.inputNum} ${
                  faseDriver === "leg" && faseRefusal !== null ? styles.inputBad : ""
                }`}
                inputMode="decimal"
                autoComplete="off"
                placeholder="z. B. 500"
                aria-describedby="fase-leg-hint"
                aria-invalid={(faseDriver === "leg" && faseRefusal !== null) || undefined}
                value={faseDriver === "leg" ? faseText : ""}
                onChange={(event) => {
                  setFaseDriver("leg");
                  setFaseText(event.target.value);
                  setFasePristine(false);
                }}
                onKeyDown={faseKey}
              />
              <span
                id="fase-leg-hint"
                className={`${styles.hint} ${faseDriver === "leg" && faseRefusal !== null ? styles.hintBad : ""}`}
              >
                {faseDriver === "leg" ? (
                  (faseRefusal ?? "in mm, ab dem gedachten Eckpunkt")
                ) : wouldBe !== null ? (
                  <>
                    ergibt <span className="num">{formatMm(wouldBe.legAMm)}</span>
                  </>
                ) : (
                  "in mm, ab dem gedachten Eckpunkt"
                )}
              </span>
            </div>

            <div className={styles.field}>
              <label className={`label ${styles.fieldLabel}`} htmlFor="fase-slant">
                Länge der Schräge
              </label>
              <input
                id="fase-slant"
                className={`${styles.input} ${styles.inputNum} ${
                  faseDriver === "slant" && faseRefusal !== null ? styles.inputBad : ""
                }`}
                inputMode="decimal"
                autoComplete="off"
                placeholder="z. B. 707"
                aria-describedby="fase-slant-hint"
                aria-invalid={(faseDriver === "slant" && faseRefusal !== null) || undefined}
                value={faseDriver === "slant" ? faseText : ""}
                onChange={(event) => {
                  setFaseDriver("slant");
                  setFaseText(event.target.value);
                  setFasePristine(false);
                }}
                onKeyDown={faseKey}
              />
              <span
                id="fase-slant-hint"
                className={`${styles.hint} ${faseDriver === "slant" && faseRefusal !== null ? styles.hintBad : ""}`}
              >
                {faseDriver === "slant" ? (
                  (faseRefusal ?? "in mm")
                ) : wouldBe !== null ? (
                  <>
                    ergibt <span className="num">{formatMm(diagonalMm(wouldBe.legAMm, wouldBe.legBMm))}</span>
                  </>
                ) : (
                  "in mm"
                )}
              </span>
            </div>

            {wouldBe !== null ? (
              <p className={styles.faseLine} data-testid="draft-fase-legs">
                Schenkel <span className="num">{formatMm(wouldBe.legAMm)}</span> an Wand{" "}
                <span className="tabular">{displayWallNumber(numbered, fasePlace.wall)}</span> ·{" "}
                <span className="num">{formatMm(wouldBe.legBMm)}</span> an Wand{" "}
                <span className="tabular">{displayWallNumber(numbered, fasePlace.next)}</span>
              </p>
            ) : null}
            {preview !== null && wouldBe !== null ? (
              <p className={styles.faseEcho} data-testid="draft-fase-echo">
                Schräge <span className="num">{formatMm(diagonalMm(wouldBe.legAMm, wouldBe.legBMm))}</span> · Wand{" "}
                <span className="tabular">{displayWallNumber(preview, fasePlace.wall)}</span> belegbar{" "}
                <span className="num">{formatMm(usableMm(preview, fasePlace.wall))}</span> · Wand{" "}
                <span className="tabular">{displayWallNumber(preview, fasePlace.next)}</span> belegbar{" "}
                <span className="num">{formatMm(usableMm(preview, fasePlace.next))}</span>
              </p>
            ) : null}
            <span className={styles.hint}>
              {existing !== undefined ? "Die Schräge ist" : "Die Schräge wird"} Wand{" "}
              <span className="tabular">{chamferWallNumber(numbered, fasePlace.wall)}</span>
              {/* The seam's slant is the last number and moves nothing on. */}
              {fasePlace.next > fasePlace.wall
                ? existing !== undefined
                  ? ", die Wände danach sind aufgerückt."
                  : ", die Wände danach rücken auf."
                : "."}
            </span>

            <div className={styles.faseActions}>
              {/* Held, not disabled, like „Ecke entfernen“: the reason is
                  the point, and the keyboard has to reach it. */}
              <Button
                ref={faseSetButton}
                variant="tinte"
                aria-disabled={faseReason !== null || running ? "true" : undefined}
                aria-describedby={faseReason !== null ? FASE_REASON : undefined}
                onClick={setFase}
              >
                Fase setzen
              </Button>
              {existing !== undefined ? (
                <Button onClick={removeFase} disabled={running}>
                  Fase entfernen
                </Button>
              ) : null}
            </div>
            {faseReason !== null ? (
              <span id={FASE_REASON} className={styles.hint}>
                {faseReason}
              </span>
            ) : null}
          </>
        )}
      </div>
    );

  const cornerPanel =
    corner === null || removal === null ? null : (
      <>
        <p className={`label ${styles.sideHead}`}>
          Ecke <span className="tabular">{corner + 1}</span>
        </p>
        {/* Held, not disabled: the reason is the point, and a disabled
            button would take it out of reach of the keyboard. */}
        <Button
          aria-disabled={removal !== "ok" || running ? "true" : undefined}
          aria-describedby={CORNER_HINT}
          onClick={() => {
            if (questionHoldsSteps()) return;
            const next = removeCorner(draft, corner);
            commit(next.draft);
            draftStore.getState().select(next.wall);
            focusWall.current = next.wall;
          }}
        >
          Ecke entfernen
        </Button>
        <span id={CORNER_HINT} className={styles.hint}>
          {CORNER_HINTS[removal]}
        </span>
        {removal === "end" ? (
          // A loose end grows the walk instead (19.10.2026): the new wall a
          // metre across the end wall, pulled to size afterwards.
          <>
            <Button
              onClick={() => {
                if (questionHoldsSteps()) return;
                const target = appendTarget(draft, corner);
                if (target) commit(appendWall(draft, corner, target));
                draftStore.getState().select(null);
              }}
              disabled={appendTarget(draft, corner) === null || running}
            >
              Wand anhängen
            </Button>
            <span className={styles.hint}>
              Setzt hier eine neue Wand an — wie das Ziehen am Endpunkt. Danach ziehen und ihr Maß tippen.
            </span>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                const target = notchTarget(draft, corner);
                const deselect = () => draftStore.getState().select(null);
                if (target) askOrCommit(pullCorner(draft, corner, target), deselect);
                else deselect();
              }}
              disabled={notchTarget(draft, corner) === null || running}
            >
              Ecke einziehen
            </Button>
            <span className={styles.hint}>Legt an dieser Ecke eine neue Kante an — wie das Ziehen an der Ecke.</span>
          </>
        )}
        {faseSection}
      </>
    );

  const wallPanel = (
    <div className={styles.wallPanel}>
      {cornerPanel !== null ? (
        cornerPanel
      ) : wall === null || selected === null ? (
        <p className={styles.idle}>
          Keine Wand gewählt. Klick eine Wand an, um ihr Maß und eine
          Beschriftung zu setzen — oder eine Ecke, um sie zu{" "}
          {faseMode ? "entfernen oder abzuschrägen." : "entfernen."}
        </p>
      ) : (
        <>
          {/* Counted as the person sees the walls: a slant before this one
              is a wall of its own (design 2026-10-18 §5.1). */}
          <p className={`label ${styles.sideHead}`}>Wand {displayWallNumber(draft, selected)}</p>
          <div className={styles.field}>
        {/* Label left, what the wall carries right — „MASS … gelesen“, where
            the board's „Maß“ field says it (Task 6). From a Stand only: the
            other ways in have one word at most, and say it in the hint. */}
        <div className={styles.fieldHead}>
          <label className={`label ${styles.fieldLabel}`} htmlFor="wall-length">
            Maß
          </label>
          {fromStand ? (
            <span className={styles.fieldState} data-testid="draft-measure-state">
              {measureWord({
                lengthMm: wall.lengthMm,
                lengthSource: wall.readMm != null && wall.readMm === wall.lengthMm ? "read" : "manual",
              })}
            </span>
          ) : null}
        </div>
        <input
          id="wall-length"
          className={`${styles.input} ${styles.inputNum} ${lengthRefused ? styles.inputBad : ""}`}
          inputMode="decimal"
          autoComplete="off"
          placeholder="z. B. 10,20 oder 10200"
          value={lengthText}
          // Left, or Enter: the figure counts as typed, and only now is it
          // asked whether the chamfers on its wall still fit (`setWall`).
          onBlur={() => {
            settleChamfers(true);
            askAboutLength(selected, false);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            // Prevented: the question may take the focus in this very
            // keydown, and the Enter's keypress would then land on its first
            // button and answer it unasked.
            event.preventDefault();
            settleChamfers(true);
            askAboutLength(selected, true);
          }}
          aria-describedby={shownDoubt !== null ? "wall-length-doubt" : undefined}
          onChange={(event) => {
            // The field would show a figure the drawing does not carry.
            if (questionHoldsSteps()) return;
            const text = event.target.value;
            setLengthText(text);
            setDoubt(null);
            // An empty field is „ich weiß es nicht" and takes the claim
            // off the wall again; a figure outside the band changes
            // nothing until it becomes one.
            if (text.trim() === "") setWall(selected, { lengthMm: null });
            else {
              const parsed = parseLengthMm(text);
              if (parsed.ok) setWall(selected, { lengthMm: parsed.mm });
            }
          }}
        />
        {shownDoubt !== null ? (
          <LengthDoubt
            // A new mount for every ask that takes the focus — it is taken on mount.
            key={shownDoubt.focusTick}
            autoFocus={shownDoubt.focusTick > 0}
            questionId="wall-length-doubt"
            mm={shownDoubt.mm}
            suggestionMm={shownDoubt.suggestionMm}
            onAnswer={answerLength}
            onDismiss={dismissLength}
          />
        ) : null}
        <span className={`${styles.hint} ${lengthRefused ? styles.hintBad : ""}`}>
          {lengthRefusal ??
            (!fromStand && wall.readMm != null && wall.readMm === wall.lengthMm
              ? "in m oder mm · gelesen — so stand es auf dem Blatt"
              : "in m oder mm")}
        </span>
      </div>

      <div className={styles.field}>
        <label className={`label ${styles.fieldLabel}`} htmlFor="wall-label">
          Beschriftung
        </label>
        <input
          id="wall-label"
          className={styles.input}
          autoComplete="off"
          placeholder="z. B. Haustüre"
          value={wall.label ?? ""}
          onChange={(event) =>
            setWall(selected, {
              label: event.target.value.trim() === "" ? null : event.target.value,
            })
          }
        />
        <span className={styles.hint}>Ein Ort, kein Maß</span>
      </div>

          {selected === draft.walls.length - 1 && !draft.closed ? (
            // The wall ends loose (Task 17): no corner to pull in there, so
            // the walk grows instead — on the one-wall start the only way
            // on, and here where „Wand anklicken“ led. The new wall is
            // picked, so its figure goes in next, and the button stays
            // with the end for the wall after it.
            <>
              <Button
                onClick={() => {
                  if (questionHoldsSteps()) return;
                  const target = appendTarget(draft, selected + 1);
                  if (target === null) return;
                  const next = appendWall(draft, selected + 1, target);
                  commit(next);
                  draftStore.getState().select(next.walls.length - 1);
                  sayStep(`Wand ${displayWallNumber(next, next.walls.length - 1)} angehängt, ohne Maß.`);
                }}
                disabled={appendTarget(draft, selected + 1) === null || running}
              >
                Wand anhängen
              </Button>
              <span className={styles.hint}>
                Setzt am Ende dieser Wand eine neue an — wie das Ziehen am Endpunkt. Danach ihr Maß tippen.
              </span>
            </>
          ) : (
            <>
              {/* The one gesture that adds walls, as a control a person
                  can find and a keyboard can reach — the drag on the
                  corner handle is the same move for a mouse. Aims at the
                  far corner of this wall, a quarter of the way in; the
                  arrow keys on the walls take it from there. */}
              <Button
                onClick={() => {
                  const at = selected + 1;
                  const target = notchTarget(draft, at);
                  if (target) askOrCommit(pullCorner(draft, at, target));
                }}
                disabled={notchTarget(draft, selected + 1) === null || running}
              >
                Ecke einziehen
              </Button>
              <span className={styles.hint}>
                Legt am Ende dieser Wand eine neue Kante an — dieselbe Geste wie das
                Ziehen an der Ecke.
              </span>
            </>
          )}

          <Button
            onClick={() => {
              if (questionHoldsSteps()) return;
              commit(deleteWall(draft, selected));
              draftStore.getState().select(null);
            }}
            disabled={draft.walls.length < 2 || running}
          >
            Wand entfernen
          </Button>
          <span className={styles.hint}>
            Entfernen öffnet den Umlauf hier — so sagst du, dass die Zeichnung an
            dieser Stelle abbricht.
          </span>
        </>
      )}
    </div>
  );

  return (
    <div className={styles.screen} data-reference={reference}>
      <header className={styles.head}>
        <PageTitle className={[styles.title, forCase === null ? styles.placeTitle : null].filter(Boolean).join(" ")}>
          {fromStand ? "Form ändern" : fromReading ? "Umriss bearbeiten" : "Umriss zeichnen"}
        </PageTitle>
        {/* One sentence (UX review 26.10.2026, B-6): the way in, nothing more —
            what else there is to know about it waits under „?“ in the stage. */}
        <p className={styles.lede}>{ledeSentence}</p>
      </header>

      {/* There before anything is said into it — a region that arrives full
          is not reliably read out. */}
      <p role="status" className="visually-hidden">
        {stepSaid}
      </p>

      {seedProblem !== null ? (
        <StateMessage
          tone="conflict"
          title={seedProblem}
          action={
            <Link className="inline-link" to={`/umriss?case=${encodeURIComponent(forCase ?? "")}`}>
              Selbst zeichnen
            </Link>
          }
        />
      ) : null}

      {standProblem === "none" ? (
        <StateMessage
          id={HELD_REASON}
          title="Dieser Vorgang hat noch keinen Stand — es gibt keine Form zu ändern."
          action={
            <Link className="inline-link" to={`/umriss?case=${encodeURIComponent(forCase ?? "")}`}>
              Umriss zeichnen
            </Link>
          }
        />
      ) : standProblem === "failed" ? (
        // Neutral like every load failure; the way on is another try.
        <StateMessage
          id={HELD_REASON}
          title="Die Form des Stands lässt sich gerade nicht laden."
          action={
            <Button variant="quiet" flush onClick={() => void stand.refetch()}>
              Erneut versuchen
            </Button>
          }
        />
      ) : standProblem === "unfit" ? (
        <StateMessage
          id={HELD_REASON}
          tone="conflict"
          title="Die Form dieses Stands lässt sich hier nicht öffnen."
          action={
            <Link className="inline-link" to={`/vorgang/${forCase}/planung`}>
              Zur Planung →
            </Link>
          }
        />
      ) : null}

      {frozen !== null && standProblem === null ? (
        <StateMessage
          id={HELD_REASON}
          title={frozen === "sent" ? FACADE_SENT : FACADE_CANCELLED}
          action={
            <Link className="inline-link" to={`/vorgang/${forCase}`}>
              Zur Akte
            </Link>
          }
        />
      ) : null}

      {standing ? (
        <StateMessage
          id={HELD_REASON}
          title="Dieser Vorgang hat schon einen Stand."
          action={
            <Link className="inline-link" to={`/vorgang/${forCase}/planung`}>
              Zur Planung →
            </Link>
          }
        >
          Ein Umriss von Hand wird nur zum ersten Stand — weiter geht es in der Planung.
        </StateMessage>
      ) : missing ? (
        <StateMessage
          id={HELD_REASON}
          tone="conflict"
          title="Diesen Vorgang gibt es nicht — oder er liegt inzwischen bei jemand anderem."
          action={
            <Link className="inline-link" to="/vorgaenge">
              Zum Register
            </Link>
          }
        />
      ) : unreachable ? (
        // Neutral, like every load failure (start, register, record): red is
        // for refusals. The drawing stays; only „Übernehmen" waits.
        <StateMessage
          id={HELD_REASON}
          title="Der Vorgang lässt sich gerade nicht laden."
          action={
            <Button variant="quiet" flush onClick={() => void target.refetch()}>
              Erneut versuchen
            </Button>
          }
        >
          Übernehmen wartet, bis er antwortet — erst dann ist klar, ob er schon einen Stand hat.
        </StateMessage>
      ) : null}

      {/* A drawing this browser kept before a reload (idea 12). Asked, not
          put on the board: what is there now stays until somebody chooses. */}
      {offered !== null ? (
        <StateMessage strong title="Ein Entwurf ist noch nicht übernommen.">
          {keptSummary(offered.draft)} — gesichert in diesem Browser. Bis du wählst, wird nichts Neues gesichert.
          <div className={styles.keptActions}>
            <Button onClick={restoreKept}>{`Entwurf vom ${formatDateTime(offered.savedAt)} wiederherstellen`}</Button>
            <Button variant="quiet" onClick={discardKept}>
              Verwerfen
            </Button>
          </div>
        </StateMessage>
      ) : null}

      {/* Said above the board, not held: the person may well want to change the
          shape, and the figure is settled on the Planung without it. */}
      {fromStand && frozen === null && boardShown && disputed.length > 0 ? (
        <DisputedFigureNotice
          title="Maß passt nicht zur Skizze"
          findings={disputed}
          closing="Diese Wand ist hier mit der getippten Zahl gezeichnet. Auf der Planung lässt sie sich klären, ohne die Form zu ändern."
          action={
            <Link className="inline-link" to={`/vorgang/${forCase}/planung`}>
              Zur Planung
            </Link>
          }
        />
      ) : null}

      {/* The drawing is the stage (UX review 26.10.2026, B-6): its head
          carries the title, the start shape and why „Übernehmen“ waits; the
          surface takes the window's height; under it one line of what the
          drawing says. Name and „Übernehmen“ come last in the markup — the
          keyboard meets them once the drawing is done (R3-10) — and stand,
          wide, top right in the head, where they stay while the stage
          scrolls (`.stage > .toolbar`). */}
      <section
        className={styles.stage}
        aria-labelledby={STAGE_TITLE}
        data-naming={forCase === null ? "true" : "false"}
      >
        <header className={styles.stageHead}>
          <div className={styles.stageLead}>
            {/* The mesh marker before a section title (design 2026-10-24 §8.6). */}
            <h2 id={STAGE_TITLE} className={styles.stageTitle}>
              <span className="marke" aria-hidden="true" />
              Grundriss
            </h2>
            {loaded ? (
              <span className={`label ${styles.count}`}>
                <span className="tabular">{measured}</span> von <span className="tabular">{draft.walls.length}</span> bemaßt
              </span>
            ) : null}
            {loaded ? (
              <>
                {/* In the stage's head, before the board, so it is met before
                    the first gesture (Task 17) — on a phone too, where the
                    tools stack under the board. Gone where the form comes
                    from a reading or a Stand. */}
                {startShape !== null ? (
                  <div className={styles.start} ref={startGroupRef}>
                    <span id={START_LABEL} className={`label ${styles.startLabel}`}>
                      Ausgangsform
                    </span>
                    {/* Manual: a new start throws work away, and must not happen to
                        somebody passing over it with the arrows (review T17-2). */}
                    <SegmentedControl
                      labelledBy={START_LABEL}
                      options={START_OPTIONS}
                      value={startShape}
                      onChange={chooseStart}
                      disabled={running}
                      activation="manual"
                    />
                  </div>
                ) : null}
                {referenceSrc !== null || underlayAvailable ? (
                  <Button
                    variant="quiet"
                    className={styles.referenceToggle}
                    aria-expanded={referenceShown}
                    aria-controls={underlayAvailable ? undefined : "draft-reference"}
                    onClick={() => {
                      setReferenceShown((shown) => !shown);
                      setStackedView("board");
                    }}
                  >
                    {referenceShown ? "Vorlage ausblenden" : "Vorlage zeigen"}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
          {/* Why „Übernehmen“ waits, in the stage's head beside it (B-6). Kept
              once a figure is typed, unseen (UX-Gutachten 4, N-5): gone, the
              head would shrink and the board and its tools move up under the
              pointer. */}
          {!blocked ? (
            <p
              id={unmeasured ? HELD_REASON : undefined}
              hidden={!unmeasured}
              className={styles.footerHold}
              data-role="needs-measure"
              data-reserved={holdStood || undefined}
            >
              {fromReading
                ? "Noch kein Maß — zu dieser Form hat die Lesung keine Maße gelesen. Tipp die Länge mindestens einer Wand ein, oder geh zurück, nimm die Lesart und lass ihre Maße lesen; die kommen danach in den Editor mit."
                : "Noch kein Maß — tipp die Länge mindestens einer Wand ein, sonst kann kein Rost liegen."}
            </p>
          ) : null}
          {editability === "approved" ? (
            // Said again beside the button that does it: the address may have
            // been opened without the question in front of it.
            <p className={styles.headNote}>{releaseLossSentence(null, null)}</p>
          ) : null}
        </header>

        {loading !== null ? (
          // The stage's own geometry while the form is on its way — never a
          // loading line (Task 10, B-4).
          <StageBones label={loading} />
        ) : loaded ? (
          <>
            {/* Maximized, this block is the overlay — board left, tools and
                wall panel right (A3, Entscheid Denis 23.09.2026). No zoom here:
                the drawing is always fitted, so bigger is the whole gain. */}
            <div
              className={styles.workspace}
              data-role="draft-workspace"
              data-maximized={maximized}
              data-reference={reference}
              data-view={reference ? stackedView : "board"}
            >
              {referenceSrc !== null ? (
                // Stacked only (CSS): the drawing and the board in one place,
                // one at a time — at 390 px the two were never in view together.
                <SegmentedControl
                  label="Ansicht"
                  className={styles.viewToggle}
                  options={VIEW_OPTIONS}
                  value={reference ? stackedView : "board"}
                  onChange={showStacked}
                />
              ) : null}
              {reference && fromReading && referenceSrc !== null ? (
                <figure id="draft-reference" className={styles.reference}>
                  <div className={styles.referenceSheet} data-blatt="">
                    {referenceFailed ? (
                      <p className={styles.referenceFailed}>Das Original lässt sich gerade nicht laden.</p>
                    ) : (
                      <img
                        className={styles.referenceImage}
                        src={referenceSrc}
                        alt=""
                        onError={() => setReferenceFailed(true)}
                      />
                    )}
                  </div>
                  <figcaption className={styles.referenceCaption}>
                    <span>
                      <span className="label">Original</span> Seite <span className="tabular">1</span>
                    </span>
                    <span>Die Seite, die gelesen wurde — zum Abgleich.</span>
                  </figcaption>
                </figure>
              ) : reference && drawing !== null && forCase !== null ? (
                <figure id="draft-reference" className={styles.reference}>
                  {/* Two drawings or more: which one is traced (UX gate round 4,
                      #4) — it was always the first, or the one a link named. */}
                  {documents.length > 1 ? (
                    <SelectField
                      id="draft-template"
                      label="Vorlage"
                      value={drawing.id}
                      options={documents.map((document) => ({ value: document.id, label: document.originalFilename }))}
                      onChange={chooseDrawing}
                    />
                  ) : null}
                  <div className={styles.referenceSheet} data-blatt="">
                    {referenceFailed ? (
                      <p className={styles.referenceFailed}>Die Vorlage lässt sich gerade nicht laden.</p>
                    ) : (
                      // Decoration, `alt=""` (final review of Plan D): the
                      // caption names the file, visible — said twice otherwise.
                      <img
                        key={drawing.id}
                        className={styles.referenceImage}
                        src={casePageUrl(forCase, drawing.id, 1)}
                        alt=""
                        onError={() => setReferenceFailed(true)}
                      />
                    )}
                  </div>
                  <figcaption className={styles.referenceCaption}>
                    <span>
                      {documents.length > 1 ? null : (
                        <>
                          <span className="label">Vorlage</span> <span>{drawing.originalFilename}</span> ·{" "}
                        </>
                      )}
                      Seite <span className="tabular">1</span>
                      {drawing.pageCount !== null && drawing.pageCount > 1 ? (
                        <>
                          {" "}
                          von <span className="tabular">{drawing.pageCount}</span>
                        </>
                      ) : null}
                    </span>
                    <span>Nur zum Nachzeichnen — gelesen wird hier nichts.</span>
                    {referenceFailed ? null : (
                      <a
                        className="inline-link"
                        href={casePageUrl(forCase, drawing.id, 1)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Groß ansehen
                      </a>
                    )}
                  </figcaption>
                </figure>
              ) : null}
              <div className={styles.boardFrame}>
                {figureAsk !== null ? (
                  <FigureLossAsk
                    key={figureAsk.id}
                    className={styles.figureAsk}
                    questionId="figure-loss-ask"
                    figures={figureAsk.lost.map(({ wallIndex, lengthMm, read }) => ({
                      wallNumber: displayWallNumber(figureAsk.base, wallIndex),
                      lengthMm,
                      read,
                    }))}
                    onCancel={keepFigures}
                    onGoAhead={dropFigures}
                  />
                ) : null}
                <OutlineDraft
                  draft={draft}
                  onDraft={boardStep}
                  onGestureEnd={askAboutDrag}
                  selectedWall={selected}
                  onSelectWall={selectWall}
                  selectedCorner={corner}
                  onSelectCorner={(index) => draftStore.getState().selectCorner(index)}
                  cornerHintId={CORNER_HINT}
                  underlay={underlay}
                />
                <MaximizeButton
                  ref={maximizeRef}
                  pressed={maximized}
                  onClick={toggleMaximized}
                  className={styles.maximize}
                />
              </div>

              <div className={styles.side}>
                {/* Beside a drawing the wall's Maß stands right under it (round-5
                    screen check, R5-4): read the figure off the drawing, type it
                    below. The tools follow the fields there; without a drawing
                    they head the column and stay put when a wall is picked. */}
                {reference ? (
                  <>
                    {wallPanel}
                    {tools}
                  </>
                ) : (
                  <>
                    {tools}
                    {wallPanel}
                  </>
                )}
                {/* That a chamfer went, and why — beside the drawing it went
                    from, in the board's column also when maximized. Read out by
                    the live region at the foot of the screen, not by this box. */}
                {/* Maximized, the line under the surface is behind the
                    overlay: where the browser keeps nothing, the overlay says
                    it once itself (final review, M-11). */}
                {maximized && !keeps ? <p className={styles.sideNote}>{NOT_KEPT_NOTE}</p> : null}
                {faseNotice !== null ? (
                  <div data-testid="draft-fase-notice">
                    <StateMessage
                      live={false}
                      title={faseNotice}
                      onDismiss={() => {
                        // Closed, so read: nothing to hold „Übernehmen“ for.
                        setFaseNotice(null);
                        unseenDrop.current = false;
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            {/* One line under the surface: what the drawing's lines and
                figures say. The gestures wait under „?“ (B-6). */}
            <p className={styles.legend}>
              <span>{faseMode ? AXIS_FASE : AXIS}</span>
              <span data-role="legend-units">
                Maße in mm
                {draft.walls.some((wall) => wall.readMm != null && wall.readMm === wall.lengthMm) ? (
                  <>
                    {" · "}
                    <span className={styles.legendSeen} aria-hidden="true">
                      ●
                    </span>{" "}
                    gelesen
                  </>
                ) : null}
              </span>
              {/* A browser that keeps nothing: said in view, not only under
                  „?“ — a reload here loses the drawing (fix round 1 of Task
                  10). Under the surface, so it moves nothing above it. */}
              {keeps ? null : <span className={styles.notKept}>{NOT_KEPT_NOTE}</span>}
            </p>
          </>
        ) : null}

        <div className={styles.toolbar} role="group" aria-label="Übernehmen">
          {forCase === null ? (
            <div className={styles.naming}>
              <TextField
                id={CASE_NAME}
                label="Name des Vorgangs"
                value={createdCase !== null ? createdCase.title : caseName}
                onChange={(value) => setCaseName(capCaseTitle(value))}
                placeholder="z. B. Müller, Lindenallee 7"
                hint={createdCase !== null ? CASE_NAME_MADE : CASE_NAME_HINT}
                readOnly={createdCase !== null || running}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
                  // Prevented: „Übernehmen“ may ask about a long wall and put
                  // the focus on that question in this very keydown.
                  event.preventDefault();
                  if (held) handOverButton.current?.focus();
                  else void handOver();
                }}
              />
            </div>
          ) : null}
          {/* Held, not disabled, while it runs or cannot run: a disabled
              button drops the focus to <body> (global constraints). */}
          <Button
            ref={handOverButton}
            variant="primary"
            onClick={() => void handOver()}
            busy={running}
            aria-disabled={running || held ? "true" : undefined}
            aria-describedby={held ? HELD_REASON : undefined}
          >
            {running ? "Wird übernommen …" : "Übernehmen"}
          </Button>
          {fromStand && forCase !== null ? (
            <Button variant="quiet" onClick={() => leaveStand(forCase)} disabled={running}>
              Abbrechen
            </Button>
          ) : null}
          {/* What the drawing's gestures are and what „Übernehmen“ does —
              shut until asked (B-6), after the button, so the field and the
              button stay next to each other for the keyboard. */}
          <OutlineHelp>
            <p>{ledeRest}</p>
            <p className={`label ${styles.helpHead}`}>Gesten</p>
            <ul className={styles.gestures}>
              <li>
                <span className={styles.legendKey}>Ecke ziehen</span> legt eine neue Kante an
              </li>
              {draft.closed ? null : (
                <li>
                  <span className={styles.legendKey}>Endpunkt ziehen</span> verlängert den Zug
                </li>
              )}
              <li>
                <span className={styles.legendKey}>Wand ziehen</span> verschiebt sie zur Seite
              </li>
              <li>
                <span className={styles.legendKey}>Bündig ziehen</span> entfernt eine Stufe
              </li>
              <li>
                <span className={styles.legendKey}>Wand anklicken</span> öffnet Maß und Beschriftung
              </li>
              {faseMode ? (
                <li>
                  <span className={styles.legendKey}>Ecke anklicken</span> öffnet Fase und Entfernen
                </li>
              ) : null}
            </ul>
            <p className={`label ${styles.helpHead}`}>Übernehmen</p>
            <p>
              {fromStand
                ? `Übernehmen speichert die Form als neuen Stand von ${
                    target.data ? `„${target.data.title}“` : "diesem Vorgang"
                  }; die Belegung von vorher wird entfernt, die Einstellungen bleiben. Bis dahin ändert sich am Vorgang nichts.${keptNote}`
                : fromReading
                ? `Übernehmen speichert den Umriss als Stand 1 von ${
                    target.data ? `„${target.data.title}“` : "diesem Vorgang"
                  }. Jedes Maß hier gilt danach als von dir gesetzt — auch die gelesenen.${keptNote}`
                : forCase !== null
                ? `Übernehmen speichert den Umriss als Stand 1 von ${
                    target.data ? `„${target.data.title}“` : "diesem Vorgang"
                  } — kein neuer Vorgang.${keptNote}`
                : `Übernehmen legt den Vorgang an und speichert den Umriss als Stand 1.${keptNote}`}
            </p>
          </OutlineHelp>
          {waiting ? (
            <span id={HELD_REASON} className="visually-hidden">
              {fromStand && target.data !== undefined
                ? "Die Form des Stands wird noch geladen."
                : fromReading && target.data !== undefined
                ? "Der Umriss aus der Lesung wird noch geladen."
                : "Der Vorgang wird noch geladen."}
            </span>
          ) : null}
        </div>
      </section>

      {/* Floated over the foot of the maximized board: „Kalibrieren" is
          inside the overlay, and its refusal must not land behind it. */}
      <div className={styles.messages} data-maximized={maximized}>
        {calibrateNote !== null ? (
          <StateMessage tone="conflict" title="Die Skizze wurde nicht kalibriert.">
            {calibrateNote}
          </StateMessage>
        ) : null}

        {handOverError !== null ? (
          <StateMessage
            tone="conflict"
            title={fromStand ? "Die Form wurde nicht geändert." : "Der Umriss wurde nicht übernommen."}
          >
            {handOverError}
          </StateMessage>
        ) : null}
      </div>

      {ask !== null ? (
        // The focus lands on „Abbrechen“, first in the footer: an Enter
        // pressed out of habit keeps what was drawn.
        <Dialog
          title={ask.kind === "start" ? START_OVER[ask.shape] : "Von vorn beginnen?"}
          onClose={() => setAsk(null)}
          onClosed={ask.kind === "start" ? focusChosenStart : focusAfterRestart}
        >
          <p className={styles.startText}>
            {ask.kind === "start" ? "Die Zeichnung beginnt von vorn." : restartTarget} {LOST}
          </p>
          <DialogFooter>
            <Button onClick={() => setAsk(null)}>Abbrechen</Button>
            <Button
              variant="primary"
              onClick={() => (ask.kind === "start" ? startOver(ask.shape) : restartDrawing())}
            >
              {ask.kind === "start" ? "Neu beginnen" : "Von vorn"}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}

      {/* Always here, so a change inside it is announced: a chamfer set,
          taken off, or gone with a change of form or a figure. */}
      <p className="visually-hidden" role="status" data-testid="draft-announce">
        {announcement}
      </p>
    </div>
  );
}
